import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient.js";
import * as Icons from "lucide-react";
import DashboardSidebarAdmin from "../components/Dashboard/DashboardSidebarAdmin.jsx";
import DashboardNavbarAdmin from "../components/Dashboard/DashboardNavbarAdmin.jsx";

const AdminGenericModelsPage = () => {
    const { featureCode } = useParams(); // 'tts', 'social', etc.
    const [loading, setLoading] = useState(true);
    const [feature, setFeature] = useState(null);

    const [models, setModels] = useState([]);
    const [providers, setProviders] = useState([]); // NEW: State for providers
    const [allFeatures, setAllFeatures] = useState([]); // NEW: All Features for Copy Target

    // Modals State
    const [showModelModal, setShowModelModal] = useState(false);
    const [showFieldsModal, setShowFieldsModal] = useState(false);
    const [showN8nModal, setShowN8nModal] = useState(false);
    const [showConfigModal, setShowConfigModal] = useState(false); // NEW: Config Modal

    const [showButtonsModal, setShowButtonsModal] = useState(false); // NEW: External Buttons Modal
    const [showProviderModal, setShowProviderModal] = useState(false); // NEW: Provider Modal
    const [showDuplicateModal, setShowDuplicateModal] = useState(false); // NEW: Duplicate Target Modal

    // Selected Data for Modals
    const [selectedModel, setSelectedModel] = useState(null); // For Fields/N8n/Config
    const [editingModel, setEditingModel] = useState(null); // For Create/Edit Model

    // Fields Management State
    const [modelFields, setModelFields] = useState([]);
    const [fieldLoading, setFieldLoading] = useState(false);
    const [editingField, setEditingField] = useState(null); // {} for new, {id..} for edit

    // Cache for all fields to enable instant open
    const [allModelFields, setAllModelFields] = useState({});

    // External Buttons State
    const [externalButtons, setExternalButtons] = useState([]);

    // Search State
    const [searchQuery, setSearchQuery] = useState("");

    // Model Form Data

    const [modelForm, setModelForm] = useState({
        name: "",
        key: "",
        provider_id: "", // NEW: Provider Link
        video_url: "", // NEW: Demo Video
        description: "",
        price_credits: 1,
        is_active: true,
        n8n_webhook_path: "",
        n8n_static_payload: "{}",
        label_text: "",
        label_icon: "",
        label_color: "blue"
    });

    useEffect(() => {
        fetchFeatureAndModels();
    }, [featureCode]);

    const fetchFeatureAndModels = async () => {
        setLoading(true);
        try {
            const { data: featureData, error: featureError } = await supabase
                .from("app_features")
                .select("*")
                .eq("slug", featureCode)
                .single();

            if (featureError) throw featureError;
            setFeature(featureData);

            // Parse External Buttons from Description
            // Format: "Real Description|||CONFIG|||JSON_STRING"
            if (featureData.description && featureData.description.includes("|||CONFIG|||")) {
                const parts = featureData.description.split("|||CONFIG|||");
                try {
                    const buttons = JSON.parse(parts[1]);
                    setExternalButtons(Array.isArray(buttons) ? buttons : []);
                } catch (e) {
                    console.error("Failed to parse external buttons", e);
                    setExternalButtons([]);
                }
            } else {
                setExternalButtons([]);
            }


            // NEW: Fetch Providers
            const { data: providersData } = await supabase
                .from("ai_model_providers")
                .select("*")
                .order("name", { ascending: true });

            setProviders(providersData || []);

            // NEW: Fetch All Features for Duplicate Target
            const { data: allFeaturesData } = await supabase
                .from("app_features")
                .select("id, name, slug")
                .order("name");
            setAllFeatures(allFeaturesData || []);

            const { data: modelData, error: modelError } = await supabase
                .from("ai_models")
                .select("*, configuration_options") // Explicitly fetch jsonb column
                .eq("feature_code", featureCode);

            // OPTIMIZATION: Fetch ALL fields for these models upfront
            let fieldCounts = {};
            let fieldsCache = {};

            if (modelData && modelData.length > 0) {
                const modelIds = modelData.map(m => m.id);
                const { data: allFields } = await supabase
                    .from("ai_model_fields")
                    .select("*") // Fetch ALL columns
                    .in("model_id", modelIds)
                    .order("sort_order", { ascending: true });

                // Process fields
                if (allFields) {
                    allFields.forEach(f => {
                        // Count
                        fieldCounts[f.model_id] = (fieldCounts[f.model_id] || 0) + 1;

                        // Cache
                        if (!fieldsCache[f.model_id]) {
                            fieldsCache[f.model_id] = [];
                        }
                        fieldsCache[f.model_id].push(f);
                    });
                }
            }

            setAllModelFields(fieldsCache);

            console.log("Fetching models for feature:", featureCode);
            console.log("Model Data:", modelData);
            if (modelError) console.error("Model Error:", modelError);

            // Flatten for easier usage
            const enrichedModels = (modelData || []).map(m => ({
                ...m,
                feature_webhook_route: featureData.webhook_route,
                field_count: fieldCounts[m.id] || 0 // Populate field_count
            }))
                .sort((a, b) => {
                    const orderA = a.configuration_options?.sort_order ?? 9999;
                    const orderB = b.configuration_options?.sort_order ?? 9999;
                    if (orderA !== orderB) return orderA - orderB;
                    return new Date(b.created_at) - new Date(a.created_at);
                });

            setModels(enrichedModels);
        } catch (err) {
            console.error("Error", err);
        } finally {
            setLoading(false);
        }
    };

    /* ================= BUTTONS MANAGEMENT ================= */

    const handleSaveButtons = async (newButtons) => {
        try {
            // 1. Get current description (without config)
            let currentDesc = feature.description || "";
            if (currentDesc.includes("|||CONFIG|||")) {
                currentDesc = currentDesc.split("|||CONFIG|||")[0];
            }

            // 2. Append new config
            const newDesc = `${currentDesc}|||CONFIG|||${JSON.stringify(newButtons)}`;

            // 3. Save to DB
            const { error } = await supabase
                .from("app_features")
                .update({ description: newDesc })
                .eq("id", feature.id);

            if (error) throw error;

            setExternalButtons(newButtons);
            setShowButtonsModal(false);

            // Re-fetch to sync
            fetchFeatureAndModels();
            alert("Tombol berhasil disimpan!");
        } catch (err) {
            alert("Gagal menyimpan tombol: " + err.message);
        }
    };

    /* ================= CONFIGURATION MANAGEMENT ================= */

    const openConfigModal = (model) => {
        setSelectedModel(model);
        setShowConfigModal(true);
    };

    const handleSaveConfig = async (configs) => {
        try {
            // Must preserve sort_order key if it exists in the old structure
            // Configs here is likely the 'categories' array from the Modal (Wait, we need to check what Modal returns)
            // UPDATE: Check Modal logic. It calls onSave(newCats). So 'configs' is just array.

            // Preserve existing keys like cover_image by spreading old config
            const oldConfig = selectedModel.configuration_options || {};
            const baseConfig = Array.isArray(oldConfig) ? {} : oldConfig;

            const newPayload = {
                ...baseConfig, // Spread existing properties (e.g. cover_image)
                categories: configs, // Update categories
                sort_order: baseConfig.sort_order || 9999 // Update/Preserve sort_order
            };

            const { data, error } = await supabase
                .from("ai_models")
                .update({ configuration_options: newPayload })
                .eq("id", selectedModel.id)
                .select(); // Fetch back to verify

            if (error) throw error;
            console.log("Saved Config:", data);
            setShowConfigModal(false);

            // Refetch to update sort if needed (though config unlikely changes sort here)
            fetchFeatureAndModels();

        } catch (err) {
            alert("Gagal menyimpan config: " + err.message);
        }
    };

    /* ================= MODEL REORDERING ================= */
    const handleMoveModel = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === models.length - 1) return;

        const newModels = [...models];
        const swapIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap
        [newModels[index], newModels[swapIndex]] = [newModels[swapIndex], newModels[index]];

        // Optimistic UI Update
        setModels(newModels);

        try {
            // Update sort_order for ALL models to ensure consistency (simpler than selective update)
            // or just update the two swapped? for safety and "init", let's update all if they lack sort_order.
            // But to save DB calls, we will just update the two modified ones ideally.
            // HOWEVER, if other items lack sort_order, just swapping 2 valus might not work if they are undefined.
            // SO: We assign sort_order to ALL items based on their new index.

            const updates = newModels.map((m, idx) => {
                const oldConfig = m.configuration_options;
                let newConfig = {};

                if (Array.isArray(oldConfig)) {
                    // Legacy: was array, convert to object
                    newConfig = {
                        categories: oldConfig,
                        sort_order: idx + 1
                    };
                } else if (oldConfig && typeof oldConfig === 'object') {
                    // Modern: Preserve all existing keys (cover_image, etc.)
                    newConfig = {
                        ...oldConfig,
                        sort_order: idx + 1
                    };
                } else {
                    // Default
                    newConfig = {
                        categories: [],
                        sort_order: idx + 1
                    };
                }

                return supabase.from("ai_models")
                    .update({ configuration_options: newConfig })
                    .eq("id", m.id);
            });

            await Promise.all(updates);

            // No need to refetch immediately as we simulated it, but good practice eventually.
        } catch (err) {
            console.error("Reorder failed", err);
            alert("Gagal menyimpan urutan.");
            fetchFeatureAndModels(); // Revert
        }
    };

    /* ================= DUPLICATE MODEL ================= */

    // 1. Open Modal
    const handleDuplicateModelClick = (model) => {
        setSelectedModel(model);
        setShowDuplicateModal(true);
    };

    // State for duplication loading
    const [isDuplicating, setIsDuplicating] = useState(false);

    // 2. Execute Duplicate
    const executeDuplicateModel = async (targetFeatureId) => {
        if (!selectedModel || isDuplicating) return;

        // Find target feature details
        const targetFeature = allFeatures.find(f => String(f.id) === String(targetFeatureId));
        if (!targetFeature) {
            alert("Target feature not found!");
            return;
        }

        try {
            setIsDuplicating(true);

            // 1. Prepare Clean Payload
            const newKey = `${selectedModel.key}-copy-${Date.now().toString().slice(-4)}`;
            const newName = `${selectedModel.name} (Copy)`;

            const payload = {
                feature_code: targetFeature.slug,
                feature_id: targetFeature.id,
                name: newName,
                key: newKey,
                description: selectedModel.description || "",
                price_credits: selectedModel.price_credits || 0,
                n8n_webhook_path: selectedModel.n8n_webhook_path || null,
                n8n_static_payload: selectedModel.n8n_static_payload || {},
                configuration_options: selectedModel.configuration_options || [],
                status: 'development',
                is_active: false,
                provider_id: selectedModel.provider_id || null,
                video_url: selectedModel.video_url || null,
                label: selectedModel.label || null
            };

            // OPTIMIZATION: Run independent async operations in parallel
            // 1. Insert new model
            // 2. Fetch old fields (independent of new model creation)
            const [modelResult, fieldsResult] = await Promise.all([
                supabase.from("ai_models").insert(payload).select().single(),
                supabase.from("ai_model_fields").select("*").eq("model_id", selectedModel.id)
            ]);

            if (modelResult.error) throw modelResult.error;

            const newModel = modelResult.data;
            const oldFields = fieldsResult.data;

            // 2. Duplicate Fields (if any)
            if (oldFields && oldFields.length > 0) {
                const newFieldsPayload = oldFields.map(f => ({
                    model_id: newModel.id,
                    key: f.key,
                    label: f.label,
                    type: f.type,
                    placeholder: f.placeholder,
                    helper_text: f.helper_text,
                    default_value: f.default_value,
                    options: f.options,
                    is_required: f.is_required,
                    is_hidden: f.is_hidden,
                    sort_order: f.sort_order
                }));

                const { error: fieldsError } = await supabase
                    .from("ai_model_fields")
                    .insert(newFieldsPayload);

                if (fieldsError) console.error("Error duplicating fields", fieldsError);
            }

            // 3. Handle Navigation / UI Update
            setShowDuplicateModal(false);

            // If copied to SAME feature, refresh list locally for speed
            if (String(targetFeatureId) === String(feature.id)) {
                fetchFeatureAndModels();
                // Optional: Show toast success
            } else {
                // If copied to DIFFERENT feature, redirect immediately
                if (window.confirm(`Model duplicated to ${targetFeature.name}! Go to that tool?`)) {
                    window.location.href = `/dashboardadmin/models/${targetFeature.slug}`;
                }
            }

        } catch (err) {
            console.error("Duplicate failed", err);
            alert("Gagal menduplikasi model: " + err.message);
        } finally {
            setIsDuplicating(false);
        }
    };

    /* ================= MODEL CRUD ================= */

    const openCreateModal = () => {
        setEditingModel({});
        setModelForm({
            name: "",
            key: "",
            description: "",
            price_credits: 1,

            is_active: true,
            status: "active",
            n8n_webhook_path: "",
            n8n_static_payload: "{}",
            label_text: "",
            label_icon: "",
            label_icon: "",
            label_color: "blue",
            provider_id: "",
            video_url: "",
            cover_image: "" // NEW: Cover Image
        });
        setShowModelModal(true);
    };

    const openEditModal = (model) => {
        setEditingModel(model);
        setModelForm({
            name: model.name,
            key: model.key,
            description: model.description || "",
            price_credits: model.price_credits || 1,

            is_active: model.is_active,
            status: model.status || (model.is_active ? 'active' : 'inactive'),
            n8n_webhook_path: model.n8n_webhook_path || "",
            provider_id: model.provider_id || "",
            video_url: model.video_url || "",
            cover_image: model.configuration_options?.cover_image || "", // NEW: Load Image
            n8n_static_payload: (model.n8n_static_payload && typeof model.n8n_static_payload === 'object')
                ? JSON.stringify(model.n8n_static_payload, null, 2)
                : (model.n8n_static_payload || "{}"),
            // Label Parsing
            label_text: (() => {
                try {
                    if (!model.label) return "";
                    if (model.label.startsWith("{")) return JSON.parse(model.label).text || "";
                    return model.label;
                } catch { return model.label || "" }
            })(),
            label_icon: (() => {
                try {
                    if (model.label && model.label.startsWith("{")) return JSON.parse(model.label).icon || "";
                    return "";
                } catch { return "" }
            })(),
            label_color: (() => {
                try {
                    if (model.label && model.label.startsWith("{")) return JSON.parse(model.label).color || "blue";
                    return "blue";
                } catch { return "blue" }
            })()
        });
        setShowModelModal(true);
    };

    const handleModelSubmit = async (e) => {
        e.preventDefault();
        try {
            let payloadJson = {};
            try { payloadJson = JSON.parse(modelForm.n8n_static_payload); }
            catch { alert("Invalid JSON in Static Payload"); return; }

            const payload = {
                ...modelForm,
                feature_id: feature.id,
                feature_code: feature.slug,

                n8n_static_payload: payloadJson,
                provider_id: modelForm.provider_id || null,
                video_url: modelForm.video_url || null,

                // Save Configuration (Merge cover_image)
                configuration_options: {
                    ...(Array.isArray(editingModel.configuration_options) ? { categories: editingModel.configuration_options } : (editingModel.configuration_options || {})),
                    cover_image: modelForm.cover_image || null
                },

                // Label Assembly
                label: modelForm.label_text ? JSON.stringify({
                    text: modelForm.label_text,
                    icon: modelForm.label_icon,
                    color: modelForm.label_color
                }) : null,
                // Sync legacy is_active for backward compatibility
                is_active: modelForm.status === 'active' || modelForm.status === 'development'
            };

            // Remove temporary fields from payload
            delete payload.label_text;
            delete payload.label_icon;
            delete payload.label_text;
            delete payload.label_icon;
            delete payload.label_color;
            delete payload.cover_image; // Don't save to root

            if (editingModel.id) {
                const { error } = await supabase.from("ai_models").update(payload).eq("id", editingModel.id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from("ai_models").insert(payload);
                if (error) throw error;
            }

            setShowModelModal(false);
            fetchFeatureAndModels();
        } catch (err) {
            alert("Error saving model: " + err.message);
        }
    };

    // New: Handle Model Video Upload
    const handleModelVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            // Keep original name but sanitize slightly for safety
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
            const path = `models/${sanitizedName}`;

            // Check if exists
            const { data: existingFiles } = await supabase.storage.from("assets").list("models", {
                search: sanitizedName,
                limit: 1
            });

            const exists = existingFiles && existingFiles.find(f => f.name === sanitizedName);

            if (exists) {
                // Skip upload, use existing
                console.log("File exists, skipping upload:", path);
                setModelForm(prev => ({ ...prev, video_url: path }));
                alert("File dengan nama yang sama ditemukan. Menggunakan file yang sudah ada (tidak upload ulang).");
                return;
            }

            const { error } = await supabase.storage.from("assets").upload(path, file);
            if (error) {
                // Handle race condition
                if (error.statusCode === '409' || error.message.includes('Duplicate')) {
                    setModelForm(prev => ({ ...prev, video_url: path }));
                    return;
                }
                throw error;
            }

            setModelForm(prev => ({ ...prev, video_url: path }));
        } catch (err) {
            alert("Upload failed: " + err.message);
        }
    };

    // New: Handle Model Image Upload
    const handleModelImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            // Keep original name but sanitize slightly for safety
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
            const path = `models/${sanitizedName}`;

            // Check if exists
            const { data: existingFiles } = await supabase.storage.from("assets").list("models", {
                search: sanitizedName,
                limit: 1
            });

            const exists = existingFiles && existingFiles.find(f => f.name === sanitizedName);

            if (exists) {
                // Skip upload, use existing
                console.log("File exists, skipping upload:", path);
                setModelForm(prev => ({ ...prev, cover_image: path }));
                alert("File dengan nama yang sama ditemukan. Menggunakan file yang sudah ada (tidak upload ulang).");
                return;
            }

            const { error } = await supabase.storage.from("assets").upload(path, file);
            if (error) {
                // Handle race condition
                if (error.statusCode === '409' || error.message.includes('Duplicate')) {
                    setModelForm(prev => ({ ...prev, cover_image: path }));
                    return;
                }
                throw error;
            }

            setModelForm(prev => ({ ...prev, cover_image: path }));
        } catch (err) {
            alert("Upload failed: " + err.message);
        }
    };

    const handleDeleteModel = async (id) => {
        if (!window.confirm("Delete this model?")) return;
        const { error } = await supabase.from("ai_models").delete().eq("id", id);
        if (error) alert(error.message);
        else fetchFeatureAndModels();
    };

    /* ================= FIELDS MANAGEMENT ================= */

    const openFieldsModal = (model) => {
        setSelectedModel(model);
        setShowFieldsModal(true);
        // Use cached fields instantly
        setModelFields(allModelFields[model.id] || []);

        // Optional: Background refresh if needed, but per user request we rely on initial load
        // fetchFields(model.id); 
    };

    const fetchFields = async (modelId) => {
        // This function can be kept for force-refresh scenarios
        setFieldLoading(true);
        const { data } = await supabase
            .from("ai_model_fields")
            .select("*")
            .eq("model_id", modelId)
            .order("sort_order", { ascending: true });

        const newFields = data || [];
        setModelFields(newFields);

        // Update cache
        setAllModelFields(prev => ({
            ...prev,
            [modelId]: newFields
        }));

        setFieldLoading(false);
        setEditingField(null);
    };

    const handleSaveField = async (fieldData) => {
        try {
            // Explicit payload to ensure no fields are dropped or malformed
            const payload = {
                model_id: selectedModel.id,
                key: fieldData.key,
                label: fieldData.label,
                type: fieldData.type,
                placeholder: fieldData.placeholder,
                helper_text: fieldData.helper_text || "",
                default_value: fieldData.default_value,
                options: fieldData.options,
                is_required: Boolean(fieldData.is_required),
                is_hidden: Boolean(fieldData.is_hidden),
                sort_order: parseInt(fieldData.sort_order) || 0
            };

            console.log("Saving Field Payload:", payload); // Debugging

            if (fieldData.id) {
                const { error } = await supabase.from("ai_model_fields").update(payload).eq("id", fieldData.id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from("ai_model_fields").insert(payload);
                if (error) throw error;
            }
            fetchFields(selectedModel.id);
            setEditingField(null);
        } catch (err) {
            console.error("Save Error:", err);
            alert("Gagal menyimpan: " + err.message);
        }
    };

    const handleMoveField = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === modelFields.length - 1) return;

        const newFields = [...modelFields];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap locally
        const temp = newFields[index];
        newFields[index] = newFields[targetIndex];
        newFields[targetIndex] = temp;

        // Update sort_order for all fields to match index
        newFields.forEach((f, idx) => f.sort_order = idx);

        setModelFields(newFields); // Optimistic update

        // Update Global Cache Optimistically
        setAllModelFields(prev => ({
            ...prev,
            [selectedModel.id]: newFields
        }));

        // Save to DB (Update only the two affected or all is fine too but optimistically standardizing is safer)
        // To be safe and ensure continuity, we can update just the swapped ones usually,
        // but resetting all ensures no gaps. For small lists (rarely > 20 fields), Promise.all is fine.
        try {
            const updates = newFields.map(f =>
                supabase.from("ai_model_fields")
                    .update({ sort_order: f.sort_order })
                    .eq("id", f.id)
            );
            await Promise.all(updates);
        } catch (err) {
            console.error("Reorder error:", err);
            alert("Gagal menyimpan urutan.");
            fetchFields(selectedModel.id); // Revert
        }
    };

    const handleDeleteField = async (id) => {
        if (!window.confirm("Delete field?")) return;
        await supabase.from("ai_model_fields").delete().eq("id", id);
        fetchFields(selectedModel.id);
    };

    /* ================= N8N GUIDE ================= */

    const openN8nModal = (model) => {
        setSelectedModel(model);
        setShowN8nModal(true);
        fetchFields(model.id); // Auto-fetch fields so inputs are populated
    };

    /* ================= UI RENDER ================= */

    if (!featureCode) return <div className="p-8">Silakan pilih fitur dari sidebar.</div>;
    if (loading) return <div className="p-8">Sedang memuat...</div>;
    if (!feature) return <div className="p-8">Fitur '{featureCode}' tidak ditemukan.</div>;

    return (
        <div className="h-screen overflow-hidden bg-slate-50 font-sans flex">
            <DashboardSidebarAdmin />
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                <DashboardNavbarAdmin displayName="Admin" />
                <main className="flex-1 p-4 lg:p-6 overflow-y-auto scrollbar-thin">
                    {/* Compact Header with Inline Search */}
                    <div className="flex items-center justify-between gap-4 mb-4">
                        <div className="flex-shrink-0">
                            <h1 className="text-xl font-bold text-slate-900">Tool: {feature.name}</h1>
                            <p className="text-slate-500 text-xs">Kelola model, konfigurasi input field, dan panduan setup N8n.</p>
                        </div>

                        {/* Search + Buttons */}
                        <div className="flex items-center gap-2">
                            {/* Search Bar */}
                            <div className="relative w-72">
                                <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search models..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-8 pr-8 py-1.5 text-xs border border-slate-200 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none transition-all bg-white"
                                />
                                {searchQuery && (
                                    <button
                                        onClick={() => setSearchQuery("")}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                    >
                                        <Icons.X className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Action Buttons - Compact */}
                            <button onClick={() => setShowProviderModal(true)} className="bg-indigo-600 text-white px-2.5 py-1.5 rounded-md hover:bg-indigo-700 text-xs font-medium flex items-center gap-1.5 shadow-sm transition-all">
                                <Icons.Network className="w-3.5 h-3.5" /> Providers
                            </button>
                            <button onClick={() => setShowButtonsModal(true)} className="bg-purple-600 text-white px-2.5 py-1.5 rounded-md hover:bg-purple-700 text-xs font-medium flex items-center gap-1.5 shadow-sm transition-all">
                                <Icons.ExternalLink className="w-3.5 h-3.5" /> Buttons
                            </button>
                            <button onClick={openCreateModal} className="bg-blue-600 text-white px-2.5 py-1.5 rounded-md hover:bg-blue-700 text-xs font-medium flex items-center gap-1.5 shadow-sm transition-all">
                                <Icons.Plus className="w-3.5 h-3.5" /> Add
                            </button>
                        </div>
                    </div>

                    {/* Compact Models List */}
                    <div className="space-y-2">
                        {models.filter(m => {
                            const query = searchQuery.toLowerCase();
                            return m.name.toLowerCase().includes(query) ||
                                m.key.toLowerCase().includes(query) ||
                                (m.description || "").toLowerCase().includes(query);
                        }).map(m => (
                            <div key={m.id} className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm hover:shadow transition-shadow">
                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                                    {/* Info */}
                                    <div className="flex gap-2 flex-1">
                                        <div className="flex flex-col gap-0.5 pt-0.5">
                                            <button
                                                onClick={() => handleMoveModel(models.indexOf(m), 'up')}
                                                disabled={models.indexOf(m) === 0}
                                                className="p-0.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30"
                                            ><Icons.ChevronUp className="w-3 h-3" /></button>
                                            <button
                                                onClick={() => handleMoveModel(models.indexOf(m), 'down')}
                                                disabled={models.indexOf(m) === models.length - 1}
                                                className="p-0.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-30"
                                            ><Icons.ChevronDown className="w-3 h-3" /></button>
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-slate-800">{m.name}</h3>
                                                {/* LABEL BADGE RENDERER */}
                                                {(() => {
                                                    if (!m.label) return null;
                                                    let labelData = { text: m.label, icon: "", color: "blue" };
                                                    try {
                                                        if (m.label.startsWith("{")) {
                                                            const parsed = JSON.parse(m.label);
                                                            labelData = { text: parsed.text, icon: parsed.icon, color: parsed.color || "blue" };
                                                        }
                                                    } catch { }

                                                    const colors = {
                                                        blue: "bg-blue-100 text-blue-700 border-blue-200",
                                                        green: "bg-emerald-100 text-emerald-700 border-emerald-200",
                                                        orange: "bg-orange-100 text-orange-700 border-orange-200",
                                                        purple: "bg-purple-100 text-purple-700 border-purple-200",
                                                        rose: "bg-rose-100 text-rose-700 border-rose-200",
                                                    };
                                                    const c = colors[labelData.color] || colors.blue;

                                                    // Resolve Icon (Handle PascalCase, lowercase, kebab-case)
                                                    let IconComp = null;
                                                    if (labelData.icon) {
                                                        const cleanName = labelData.icon.trim();
                                                        // 1. Try exact match (e.g. "Crown")
                                                        if (Icons[cleanName]) IconComp = Icons[cleanName];
                                                        else {
                                                            // 2. Try converting kebab-case/lowercase to PascalCase (e.g. "crown"->"Crown", "message-circle"->"MessageCircle")
                                                            const pascalName = cleanName.replace(/(^|-)(\w)/g, (match, p1, p2) => p2.toUpperCase());
                                                            if (Icons[pascalName]) IconComp = Icons[pascalName];
                                                        }
                                                    }

                                                    return (
                                                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border flex items-center gap-0.5 ${c}`}>
                                                            {IconComp && <IconComp size={8} />}
                                                            {labelData.text}
                                                        </span>
                                                    );
                                                })()}
                                                {m.status === 'active' && <span className="bg-emerald-50 text-emerald-700 text-[9px] font-bold px-1.5 py-0.5 rounded border border-emerald-100">AKTIF</span>}
                                                {m.status === 'development' && <span className="bg-blue-50 text-blue-700 text-[9px] font-bold px-1.5 py-0.5 rounded border border-blue-100">DEV</span>}
                                                {m.status === 'inactive' && <span className="bg-slate-100 text-slate-500 text-[9px] font-bold px-1.5 py-0.5 rounded border border-slate-200">OFF</span>}
                                                {/* Fallback for legacy if status undefined */}
                                                {!m.status && (m.is_active ?
                                                    <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded border border-emerald-100">AKTIF</span> :
                                                    <span className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-0.5 rounded border border-slate-200">NON-AKTIF</span>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-0.5 font-mono bg-slate-50 inline-block px-1.5 py-0.5 rounded text-[10px] border border-slate-100">Key: {m.key}</div>

                                            {/* CREDIT INFO */}
                                            <div className="mt-1 text-[10px] flex flex-wrap gap-1 items-center">
                                                <span className="bg-slate-100/50 text-slate-500 px-1.5 py-0.5 rounded border border-slate-100">
                                                    Base: <strong className="text-slate-700">{m.price_credits}</strong>
                                                </span>
                                                {(() => {
                                                    let optionsCost = 0;
                                                    if (m.configuration_options?.categories) {
                                                        m.configuration_options.categories.forEach(cat => {
                                                            const defaultOpt = cat.options.find(o => o.is_default) || cat.options[0];
                                                            if (defaultOpt) optionsCost += (defaultOpt.credit_cost || 0);
                                                        });
                                                    }
                                                    return optionsCost > 0 ? (
                                                        <>
                                                            <span className="text-slate-300 mx-1">+</span>
                                                            <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded border border-blue-100">
                                                                Options: <strong>{optionsCost}</strong>
                                                            </span>
                                                            <span className="text-slate-300 mx-1">=</span>
                                                            <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded border border-emerald-100">
                                                                Total Kredit model ini: <strong>{parseInt(m.price_credits) + optionsCost}</strong>
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <span className="bg-slate-50 text-slate-400 px-2 py-0.5 rounded border border-slate-100 italic">
                                                            No extra options
                                                        </span>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-1.5 mt-2 overflow-x-auto pb-1 scrollbar-hide">
                                        <button onClick={() => openN8nModal(m)} className="flex items-center gap-1 px-2 py-1 bg-slate-800 text-white rounded text-[10px] font-semibold hover:bg-slate-900 transition-colors whitespace-nowrap">
                                            <Icons.FileJson className="w-3 h-3" /> N8N
                                        </button>

                                        <div className="h-6 w-px bg-slate-200 flex-shrink-0"></div>

                                        <button onClick={() => handleDuplicateModelClick(m)} className="flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-[10px] font-semibold hover:bg-emerald-100 transition-colors border border-emerald-200 whitespace-nowrap">
                                            <Icons.Copy className="w-3 h-3" /> Copy
                                        </button>

                                        {feature?.output_type !== 'chatbox' && (
                                            <button onClick={() => openConfigModal(m)} className="flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded text-[10px] font-semibold hover:bg-purple-100 transition-colors border border-purple-200 whitespace-nowrap">
                                                <Icons.Sliders className="w-3 h-3" /> Config ({m.configuration_options?.categories?.length || 0})
                                            </button>
                                        )}

                                        <button onClick={() => openFieldsModal(m)} className="flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-[10px] font-semibold hover:bg-indigo-100 transition-colors border border-indigo-200 whitespace-nowrap">
                                            <Icons.Settings className="w-3 h-3" /> Input ({m?.field_count || 0})
                                        </button>

                                        <div className="h-6 w-px bg-slate-200 flex-shrink-0"></div>

                                        <button onClick={() => openEditModal(m)} className="flex items-center gap-1 px-2 py-1 bg-white text-slate-600 rounded text-[10px] font-semibold hover:bg-slate-50 transition-colors border border-slate-200 whitespace-nowrap">
                                            <Icons.Edit3 className="w-3 h-3" /> Edit
                                        </button>
                                        <button onClick={() => handleDeleteModel(m.id)} className="flex items-center gap-1 px-2 py-1 bg-white text-rose-600 rounded text-[10px] font-semibold hover:bg-rose-50 transition-colors border border-slate-200 whitespace-nowrap">
                                            <Icons.Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>



                    {/* PROVIDER MANAGER MODAL */}
                    {showProviderModal && (
                        <ProviderManagerModal
                            onClose={() => setShowProviderModal(false)}
                            onUpdate={() => fetchFeatureAndModels()}
                        />
                    )}


                    {/* MODAL: DUPLICATE TARGET */}
                    {showDuplicateModal && selectedModel && (
                        <DuplicateTargetModalNew
                            features={allFeatures}
                            currentFeatureId={feature.id}
                            onClose={() => setShowDuplicateModal(false)}
                            onConfirm={executeDuplicateModel}
                            modelName={selectedModel.name}
                        />
                    )}

                    {/* MODAL: EXTRERNAL BUTTONS */}
                    {showButtonsModal && (
                        <ExternalButtonsModal
                            initialButtons={externalButtons}
                            onSave={handleSaveButtons}
                            onClose={() => setShowButtonsModal(false)}
                        />
                    )}

                    {/* MODAL: MODEL FORM */}
                    {showModelModal && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                                    <h2 className="text-lg font-bold text-slate-800">{editingModel.id ? "Edit Model" : "Buat Model Baru"}</h2>
                                    <button onClick={() => setShowModelModal(false)}><Icons.X className="w-5 h-5 text-slate-400" /></button>
                                </div>
                                <form onSubmit={handleModelSubmit} className="p-6 space-y-4">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 mb-1">Nama Model <span className="text-rose-500">*</span></label>
                                            <input
                                                value={modelForm.name}
                                                onChange={e => setModelForm({ ...modelForm, name: e.target.value })}
                                                className="w-full text-sm border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none"
                                                placeholder="Contoh: Flux Pro 1.0"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 mb-1">Provider (Optional)</label>
                                            <div className="relative">
                                                <select
                                                    value={modelForm.provider_id || ""}
                                                    onChange={e => setModelForm({ ...modelForm, provider_id: e.target.value || null })}
                                                    className="w-full text-sm border border-slate-300 rounded-lg p-2.5 pr-8 focus:border-blue-500 outline-none appearance-none bg-white"
                                                >
                                                    <option value="">-- No Provider --</option>
                                                    {providers.map(p => (
                                                        <option key={p.id} value={p.id}>{p.name}</option>
                                                    ))}
                                                </select>
                                                <Icons.ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                            </div>
                                            <p className="text-[10px] text-slate-400 mt-1">Select a provider to link assets (icon/video).</p>
                                        </div>

                                        {/* NEW: Demo Video Upload */}
                                        {/* SINGLE MEDIA ASSET (Video OR Image) */}
                                        <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                                            <label className="block text-xs font-bold text-slate-700 mb-2 uppercase flex justify-between">
                                                <span>Media Asset (Pilih Satu)</span>
                                                <div className="flex flex-col items-end">
                                                    <span className="text-[10px] text-slate-400 font-normal normal-case">Hapus aset saat ini untuk mengganti tipe.</span>
                                                    <span className="text-[10px] text-blue-500 font-bold normal-case">Rekomendasi: 700 x 700 px (1:1)</span>
                                                </div>
                                            </label>

                                            <div className="grid grid-cols-2 gap-4">
                                                {/* Video Option */}
                                                <div className={`transition-all ${modelForm.cover_image ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                                                    <div className="mb-1 flex justify-between items-center">
                                                        <span className="text-[10px] font-bold text-slate-600">Video Demo</span>
                                                    </div>
                                                    <DragDropUploader
                                                        accept="video/mp4"
                                                        onUpload={handleModelVideoUpload}
                                                        currentUrl={modelForm.video_url}
                                                        label="Drop Video"
                                                        onRemove={() => setModelForm({ ...modelForm, video_url: "" })}
                                                        disabled={!!modelForm.cover_image}
                                                    />
                                                </div>

                                                {/* Image Option */}
                                                <div className={`transition-all ${modelForm.video_url ? 'opacity-30 pointer-events-none grayscale' : ''}`}>
                                                    <div className="mb-1 flex justify-between items-center">
                                                        <span className="text-[10px] font-bold text-slate-600">Cover Image</span>
                                                    </div>
                                                    <DragDropUploader
                                                        accept="image/*"
                                                        onUpload={handleModelImageUpload}
                                                        currentUrl={modelForm.cover_image}
                                                        label="Drop Image"
                                                        onRemove={() => setModelForm({ ...modelForm, cover_image: "" })}
                                                        disabled={!!modelForm.video_url}
                                                        height="aspect-square"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-bold text-slate-700 mb-1">Model Key (Unique) <span className="text-rose-500">*</span></label>
                                            <input
                                                value={modelForm.key}
                                                onChange={e => setModelForm({ ...modelForm, key: e.target.value })}
                                                className="w-full text-sm border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none font-mono"
                                                placeholder="flux-pro-1.0"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Deskripsi</label>
                                        <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="2" value={modelForm.description} onChange={e => setModelForm({ ...modelForm, description: e.target.value })} placeholder="Deskripsi model yang dilihat user..."></textarea>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Harga per Request (Kredit)</label>
                                        <input type="number" className="w-full border rounded-lg px-3 py-2 text-sm" required value={modelForm.price_credits} onChange={e => setModelForm({ ...modelForm, price_credits: e.target.value })} />
                                    </div>

                                    <div className="pt-2">
                                        <label className="block text-xs font-bold text-slate-700 uppercase mb-1">Status Model</label>
                                        <select
                                            className="w-full border rounded-lg px-3 py-2 text-sm bg-slate-50"
                                            value={modelForm.status}
                                            onChange={e => setModelForm({ ...modelForm, status: e.target.value })}
                                        >
                                            <option value="active">Active (Publik)</option>
                                            <option value="development">Development (Admin Only)</option>
                                            <option value="inactive">Non-Aktif</option>
                                        </select>
                                    </div>

                                    <details className="group border border-slate-200 rounded-lg bg-slate-50 open:bg-white transition-colors">
                                        <summary className="flex items-center justify-between p-3 cursor-pointer select-none list-none text-xs font-bold text-slate-500 group-open:text-slate-800 group-open:border-b group-open:border-slate-100">
                                            <span>Konfigurasi Lanjutan (N8N Webhook Override)</span>
                                            <Icons.ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
                                        </summary>
                                        <div className="p-4">
                                            <div>
                                                <div className="flex justify-between">
                                                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1">N8N Webhook Path (Override)</label>
                                                    <span className="text-[10px] text-slate-400">Kosongkan untuk pakai default Fitur</span>
                                                </div>
                                                <input className="w-full border rounded-lg px-3 py-2 text-sm font-mono text-xs" value={modelForm.n8n_webhook_path} onChange={e => setModelForm({ ...modelForm, n8n_webhook_path: e.target.value })} placeholder={`Default: ${feature?.webhook_route || "(Belum ada webhook fitur)"}`} />
                                            </div>
                                        </div>
                                    </details>

                                    {/* LABEL CONFIGURATION */}
                                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                                        <h4 className="text-xs font-bold text-slate-700 uppercase mb-3 flex items-center gap-2">
                                            <Icons.Tag size={14} /> Label & Badge Configuration
                                        </h4>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 mb-1">Label Text</label>
                                                <input className="w-full border rounded px-3 py-2 text-sm bg-white" placeholder="e.g. Popular" value={modelForm.label_text} onChange={e => setModelForm({ ...modelForm, label_text: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 mb-1">
                                                    Icon Name (<a href="https://lucide.dev/icons" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Lucide</a>)
                                                </label>
                                                <input className="w-full border rounded px-3 py-2 text-sm font-mono bg-white" placeholder="e.g. Flame" value={modelForm.label_icon} onChange={e => setModelForm({ ...modelForm, label_icon: e.target.value })} />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-bold text-slate-500 mb-1">Warna Badge</label>
                                                <select className="w-full border rounded px-3 py-2 text-sm bg-white" value={modelForm.label_color} onChange={e => setModelForm({ ...modelForm, label_color: e.target.value })}>
                                                    <option value="blue">Blue (Default)</option>
                                                    <option value="green">Green (Success)</option>
                                                    <option value="orange">Orange (Attention)</option>
                                                    <option value="purple">Purple (Premium)</option>
                                                    <option value="rose">Rose (Hot/Error)</option>
                                                </select>
                                            </div>
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-2">
                                            Label ini akan muncul di samping nama model. Kosongkan <b>Label Text</b> jika tidak ingin memakai label.
                                        </p>
                                    </div>

                                    <div className="pt-2 flex justify-end gap-2">
                                        <button type="button" onClick={() => setShowModelModal(false)} className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg text-sm">Batal</button>
                                        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium">Simpan Model</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}

                    {/* MODAL: FIELD MANAGER */}
                    {showFieldsModal && selectedModel && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
                            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
                                    <div>
                                        <h2 className="text-lg font-bold text-slate-800">Kelola Input Field</h2>
                                        <p className="text-xs text-slate-500">Model: <span className="font-mono text-blue-600">{selectedModel.name}</span></p>
                                    </div>
                                    <button onClick={() => setShowFieldsModal(false)}><Icons.X className="w-5 h-5 text-slate-400" /></button>
                                </div>
                                <div className="flex-1 flex overflow-hidden">
                                    {/* Left: List */}
                                    <div className="w-1/3 border-r border-slate-200 overflow-y-auto p-4 bg-slate-50/30">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-xs font-bold text-slate-500 uppercase">Daftar Field</h3>
                                            <button onClick={() => setEditingField({ sort_order: modelFields.length, type: 'text' })} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-bold hover:bg-blue-200">+ Baru</button>
                                        </div>
                                        <div className="space-y-2">
                                            {fieldLoading ? <div className="text-xs text-slate-400">Memuat...</div> : modelFields.map((f, idx) => (
                                                <div key={f.id} className={`p-3 rounded-lg border flex gap-2 items-center transition-all ${editingField?.id === f.id ? 'bg-blue-50 border-blue-200 shadow-sm' : 'bg-white border-slate-200 hover:border-blue-300'}`}>
                                                    <div className="flex flex-col gap-0.5">
                                                        <button
                                                            disabled={idx === 0}
                                                            onClick={(e) => { e.stopPropagation(); handleMoveField(idx, 'up'); }}
                                                            className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-400 p-0.5"
                                                        >
                                                            <Icons.ChevronUp size={12} />
                                                        </button>
                                                        <button
                                                            disabled={idx === modelFields.length - 1}
                                                            onClick={(e) => { e.stopPropagation(); handleMoveField(idx, 'down'); }}
                                                            className="text-slate-400 hover:text-blue-600 disabled:opacity-30 disabled:hover:text-slate-400 p-0.5"
                                                        >
                                                            <Icons.ChevronDown size={12} />
                                                        </button>
                                                    </div>
                                                    <div className="flex-1 cursor-pointer" onClick={() => setEditingField(f)}>
                                                        <div className="flex justify-between items-start">
                                                            <span className="font-bold text-slate-700 text-sm flex items-center gap-1">
                                                                {f.label}
                                                                {f.is_required && <Icons.CheckCircle size={12} className="text-emerald-500" />}
                                                            </span>
                                                            <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 font-mono">{f.type}</span>
                                                        </div>
                                                        <div className="text-[10px] text-slate-400 font-mono mt-1">{f.key}</div>
                                                    </div>
                                                </div>
                                            ))}
                                            {modelFields.length === 0 && !fieldLoading && <p className="text-xs text-slate-400 text-center py-4">Belum ada field.</p>}
                                        </div>
                                    </div>
                                    {/* Right: Edit Form */}
                                    <div className="flex-1 overflow-y-auto p-6 bg-white">
                                        {editingField ? (
                                            <FieldForm
                                                key={editingField.id || 'new'}
                                                initialData={editingField}
                                                onSave={handleSaveField}
                                                onDelete={handleDeleteField}
                                                onCancel={() => setEditingField(null)}
                                                featureOutput={feature?.output_type}
                                                usedTypes={modelFields.map(f => f.type)}
                                            />
                                        ) : (
                                            <div className="h-full flex flex-col items-center justify-center text-slate-400">
                                                <Icons.Settings className="w-10 h-10 mb-2 opacity-20" />
                                                <p className="text-sm">Pilih field untuk diedit atau buat baru.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* MODAL: N8N GUIDE */}
                    {showN8nModal && selectedModel && (
                        <N8nGuideModal
                            model={selectedModel}
                            fields={modelFields}
                            feature={feature}
                            onClose={() => setShowN8nModal(false)}
                        />
                    )}

                    {/* MODAL: CONFIGURATION MANAGER */}
                    {showConfigModal && selectedModel && (
                        <ConfigurationManagerModal
                            initialConfigs={selectedModel.configuration_options || []}
                            onSave={handleSaveConfig}
                            onClose={() => setShowConfigModal(false)}
                        />
                    )}
                </main>
            </div>
        </div>
    );
};

// Sub-component: Configuration Manager
const ConfigurationManagerModal = ({ initialConfigs, onSave, onClose }) => {
    // Structure: [ { id, name, target_keys: [], options: [ { id, label, credit, values: {}, is_default } ] } ]
    // OR New Structure: { sort_order: 1, categories: [...] }

    // Normalize input to just categories array for the UI
    const getValues = (input) => {
        if (Array.isArray(input)) return input;
        if (input && Array.isArray(input.categories)) return input.categories;
        return [];
    };

    const [categories, setCategories] = useState(getValues(initialConfigs));
    const [editingCategory, setEditingCategory] = useState(null); // null means list view, object means edit form
    const [editingOption, setEditingOption] = useState(null); // null means category view, object means option form

    // --- LEVEL 1: CATEGORY LIST ---
    const handleAddCategory = () => {
        setEditingCategory({ id: Date.now(), name: "", target_keys: [] });
    };

    const handleDeleteCategory = (idx) => {
        if (!confirm("Hapus kategori ini beserta semua opsinya?")) return;
        const newCats = [...categories];
        newCats.splice(idx, 1);
        setCategories(newCats);
    };

    // --- LEVEL 2: CATEGORY FORM ---
    const handleSaveCategory = (cat) => {
        const newCats = [...categories];
        const idx = newCats.findIndex(c => c.id === cat.id);
        if (idx >= 0) newCats[idx] = cat;
        else newCats.push({ ...cat, options: [] });
        setCategories(newCats);
        setEditingCategory(null);
    };

    // --- LEVEL 3: OPTION LIST ---
    const handleAddOption = () => {
        setEditingOption({ id: Date.now(), label: "", credit_cost: 0, values: {}, is_default: false });
    };

    const handleDeleteOption = (catIdx, optIdx) => {
        const newCats = [...categories];
        newCats[catIdx].options.splice(optIdx, 1);
        setCategories(newCats);
    };

    const handleMoveOption = (catIdx, optIdx, direction) => {
        const newCats = [...categories];
        const options = newCats[catIdx].options || [];

        if (direction === 'up' && optIdx === 0) return;
        if (direction === 'down' && optIdx === options.length - 1) return;

        const targetIdx = direction === 'up' ? optIdx - 1 : optIdx + 1;

        // Swap
        [options[optIdx], options[targetIdx]] = [options[targetIdx], options[optIdx]];

        newCats[catIdx].options = options;
        setCategories(newCats);
    };

    // --- LEVEL 4: OPTION FORM ---
    const handleSaveOption = (option) => {
        const newCats = [...categories];
        const catIdx = newCats.findIndex(c => c.id === editingCategory.id);

        // Handle Default Toggle Logic (Only one default per category)
        if (option.is_default) {
            newCats[catIdx].options.forEach(o => o.is_default = false);
        }

        const optIdx = newCats[catIdx].options.findIndex(o => o.id === option.id);
        if (optIdx >= 0) newCats[catIdx].options[optIdx] = option;
        else newCats[catIdx].options.push(option);

        setCategories(newCats);
        setEditingOption(null);
    };

    // RENDERERS
    if (editingOption) {
        return (
            <OptionForm
                category={editingCategory}
                initialData={editingOption}
                onSave={handleSaveOption}
                onCancel={() => setEditingOption(null)}
            />
        );
    }

    if (editingCategory) {
        return (
            <CategoryForm
                category={editingCategory}
                onSave={handleSaveCategory}
                onCancel={() => setEditingCategory(null)}
                onEditOption={(opt) => setEditingOption(opt)}
                onAddOption={handleAddOption}
                onDeleteOption={(optIdx) => {
                    const catIdx = categories.findIndex(c => c.id === editingCategory.id);
                    handleDeleteOption(catIdx, optIdx);
                }}
                onMoveOption={(optIdx, direction) => {
                    const catIdx = categories.findIndex(c => c.id === editingCategory.id);
                    handleMoveOption(catIdx, optIdx, direction);
                }}
            />
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="text-lg font-bold text-slate-800">Konfigurasi Model</h2>
                    <button onClick={onClose}><Icons.X className="w-5 h-5 text-slate-400" /></button>
                </div>
                <div className="p-6 overflow-y-auto flex-1">
                    {categories.length === 0 ? (
                        <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl">
                            <Icons.Sliders className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500 text-sm mb-4">Belum ada kategori konfigurasi.</p>
                            <button onClick={handleAddCategory} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow hover:bg-blue-700">
                                + Buat Kategori Baru
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {categories.map((cat, idx) => (
                                <div key={cat.id} className="border border-slate-200 rounded-lg p-4 bg-white hover:border-blue-300 transition-colors group">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-bold text-slate-800">{cat.name}</h3>
                                            <div className="flex gap-2 mt-1">
                                                {cat.target_keys.map(k => (
                                                    <span key={k} className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 font-mono border border-slate-200">{k}</span>
                                                ))}
                                            </div>
                                            <p className="text-xs text-slate-500 mt-2">{cat.options?.length || 0} Opsi tersedia</p>
                                        </div>
                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button onClick={() => setEditingCategory(cat)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded"><Icons.Edit3 size={16} /></button>
                                            <button onClick={() => handleDeleteCategory(idx)} className="text-rose-600 hover:bg-rose-50 p-1.5 rounded"><Icons.Trash2 size={16} /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            <button onClick={handleAddCategory} className="w-full py-3 border-2 border-dashed border-slate-200 hover:border-blue-400 rounded-lg text-slate-500 hover:text-blue-600 font-medium text-sm transition-colors">
                                + Tambah Kategori Lain
                            </button>
                        </div>
                    )}
                </div>
                <div className="p-4 border-t bg-slate-50 flex justify-end gap-2 shrink-0">
                    <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">Batal</button>
                    <button onClick={() => onSave(categories)} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-blue-700">Simpan Perubahan</button>
                </div>
            </div>
        </div>
    );
};

// Sub-component: Config Category Form
const CategoryForm = ({ category, onSave, onCancel, onEditOption, onAddOption, onDeleteOption, onMoveOption }) => {
    const [form, setForm] = useState({ ...category });
    const [keyInput, setKeyInput] = useState("");

    const addKey = (e) => {
        e.preventDefault();
        if (keyInput.trim()) {
            setForm(prev => ({ ...prev, target_keys: [...prev.target_keys, keyInput.trim()] }));
            setKeyInput("");
        }
    };

    const removeKey = (idx) => {
        const newKeys = [...form.target_keys];
        newKeys.splice(idx, 1);
        setForm({ ...form, target_keys: newKeys });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="text-lg font-bold text-slate-800">Edit Kategori</h2>
                </div>
                <div className="p-6 overflow-y-auto flex-1 space-y-6">
                    {/* Category Details */}
                    <div className="space-y-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Nama Kategori</label>
                            <input className="w-full border rounded px-3 py-2 text-sm font-bold" autoFocus value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Contoh: Ukuran, Durasi, Gaya..." />
                            <div className="mt-2 text-[10px] text-slate-500 bg-slate-100 p-2 rounded border border-slate-200">
                                <b>Smart Icons Trigger:</b> Gunakan kata berikut di nama kategori untuk ikon otomatis:
                                <ul className="list-disc list-inside mt-1 ml-1 space-y-0.5">
                                    <li><b>Layout/Rasio:</b> ratio, dimensi, aspect, ukuran</li>
                                    <li><b>Waktu:</b> durasi, waktu, time</li>
                                    <li><b>Kualitas:</b> quality, kualitas, resolusi</li>
                                    <li><b>Gaya:</b> style, gaya</li>
                                    <li><b>Suara:</b> suara, voice, audio, tone</li>
                                </ul>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Target Keys (JSON Keys)</label>
                            <div className="flex gap-2 mb-2">
                                <input className="flex-1 border rounded px-3 py-2 text-sm font-mono text-xs" value={keyInput} onChange={e => setKeyInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addKey(e)} placeholder="Masukkan key lalu Enter (cth: width)" />
                                <button onClick={addKey} className="bg-slate-800 text-white px-3 py-2 rounded text-xs font-bold">Add Key</button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                {form.target_keys.map((k, i) => (
                                    <span key={i} className="bg-white border border-slate-300 px-2 py-1 rounded text-xs font-mono flex items-center gap-2">
                                        {k} <button onClick={() => removeKey(i)} className="text-rose-500 hover:text-rose-700">×</button>
                                    </span>
                                ))}
                                {form.target_keys.length === 0 && <span className="text-xs text-slate-400 italic">Belum ada target keys set.</span>}
                            </div>
                        </div>
                    </div>

                    {/* Options List */}
                    <div>
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-sm font-bold text-slate-700">Opsi Kategori Ini</h3>
                            <button onClick={onAddOption} className="text-xs bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg font-bold hover:bg-blue-200">+ Buat Opsi</button>
                        </div>

                        <div className="space-y-2">
                            {(category.options || []).map((opt, idx) => (
                                <div key={opt.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-slate-50">
                                    <div className="flex items-center gap-2">
                                        <div className="flex flex-col gap-0.5 mr-1">
                                            <button
                                                onClick={() => onMoveOption(idx, 'up')}
                                                disabled={idx === 0}
                                                className="text-slate-400 hover:text-blue-600 disabled:opacity-20 p-0.5"
                                            >
                                                <Icons.ChevronUp size={12} />
                                            </button>
                                            <button
                                                onClick={() => onMoveOption(idx, 'down')}
                                                disabled={idx === (category.options || []).length - 1}
                                                className="text-slate-400 hover:text-blue-600 disabled:opacity-20 p-0.5"
                                            >
                                                <Icons.ChevronDown size={12} />
                                            </button>
                                        </div>
                                        {opt.is_default && <span className="bg-emerald-100 text-emerald-700 text-[9px] px-1.5 py-0.5 rounded uppercase font-bold tracking-wider">Default</span>}
                                        <span className="font-bold text-sm text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">{opt.label}</span>
                                        {opt.description && <span className="text-xs text-slate-500 font-medium border-l border-slate-200 pl-2">{opt.description}</span>}
                                        <span className="text-xs text-slate-500 ml-1">+{opt.credit_cost} Kredit</span>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => onEditOption(opt)} className="text-blue-600 hover:underline text-xs font-medium">Edit</button>
                                        <div className="w-px h-4 bg-slate-300"></div>
                                        <button onClick={() => onDeleteOption(idx)} className="text-rose-600 hover:underline text-xs font-medium">Hapus</button>
                                    </div>
                                </div>
                            ))}
                            {(category.options || []).length === 0 && <p className="text-center text-xs text-slate-400 py-4 border border-dashed rounded-lg">Belum ada opsi.</p>}
                        </div>
                    </div>
                </div>
                <div className="p-4 border-t bg-slate-50 flex justify-end gap-2">
                    <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">Batal</button>
                    <button onClick={() => onSave(form)} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-blue-700">Simpan Kategori</button>
                </div>
            </div>
        </div>
    );
};

// Sub-component: Option Form
const OptionForm = ({ category, initialData, onSave, onCancel }) => {
    const [form, setForm] = useState({ ...initialData });

    const handleValueChange = (key, val) => {
        setForm(prev => ({
            ...prev,
            values: { ...prev.values, [key]: val }
        }));
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 bg-slate-50">
                    <h2 className="text-lg font-bold text-slate-800">{initialData.id ? "Edit Opsi" : "Buat Opsi Baru"}</h2>
                    <p className="text-xs text-slate-500">Untuk Kategori: {category.name}</p>
                </div>
                <div className="p-6 space-y-4 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Label (Singkat)</label>
                            <input className="w-full border rounded px-3 py-2 text-sm" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Contoh: 16:9" autoFocus />
                            <p className="text-[10px] text-slate-400 mt-0.5">Muncul di tombol (Max 6-8 huruf).</p>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Nama (Lengkap)</label>
                            <input className="w-full border rounded px-3 py-2 text-sm" value={form.description || ""} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Contoh: Widescreen Layout" />
                            <p className="text-[10px] text-slate-400 mt-0.5">Muncul di dalam menu.</p>
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Biaya Tambahan (Kredit)</label>
                        <input type="number" className="w-full border rounded px-3 py-2 text-sm" value={form.credit_cost} onChange={e => setForm({ ...form, credit_cost: parseInt(e.target.value) || 0 })} />
                    </div>

                    <div className="pt-2">
                        {(() => {
                            const otherDefault = category.options.find(o => o.is_default && o.id !== initialData.id);
                            const isLocked = !!otherDefault && !form.is_default;

                            return (
                                <div className={`p-3 rounded-lg border ${isLocked ? "bg-slate-100 border-slate-200" : "bg-sky-50 border-sky-100"}`}>
                                    <label className={`flex items-center gap-2 ${isLocked ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
                                        <input
                                            type="checkbox"
                                            disabled={isLocked}
                                            checked={form.is_default}
                                            onChange={e => setForm({ ...form, is_default: e.target.checked })}
                                            className="rounded text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm font-bold text-slate-700">Set sebagai Default</span>
                                    </label>
                                    {isLocked ? (
                                        <p className="text-[10px] text-rose-500 mt-1 font-medium">
                                            Status Default sedang digunakan oleh opsi: <b>{otherDefault.label}</b>. <br />
                                            (Unset opsi tersebut dulu untuk memilih ini)
                                        </p>
                                    ) : (
                                        <p className="text-[10px] text-sky-600 mt-1">
                                            {form.is_default
                                                ? "Opsi ini akan terpilih otomatis."
                                                : "User harus memilih opsi ini secara manual."}
                                        </p>
                                    )}
                                </div>
                            );
                        })()}
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                        <h4 className="text-xs font-bold text-slate-700 uppercase mb-3 border-b border-slate-200 pb-2">Values Injection</h4>
                        {category.target_keys.length === 0 ? (
                            <p className="text-xs text-rose-500 italic">Kategori ini belum punya Target Keys. Edit kategori dulu.</p>
                        ) : (
                            <div className="space-y-3">
                                {category.target_keys.map(k => (
                                    <div key={k}>
                                        <label className="block text-[10px] font-bold text-slate-500 font-mono mb-1">{k}</label>
                                        <input
                                            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono text-slate-700 focus:border-blue-500 outline-none"
                                            value={form.values?.[k] || ""}
                                            onChange={e => handleValueChange(k, e.target.value)}
                                            placeholder={`Nilai untuk ${k}...`}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-4 border-t bg-slate-50 flex justify-end gap-2">
                    <button onClick={onCancel} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">Batal</button>
                    <button onClick={() => onSave(form)} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-blue-700">Simpan Opsi</button>
                </div>
            </div>
        </div>
    );
};

// Sub-component: Field Form
const FieldForm = ({ initialData, onSave, onDelete, onCancel, featureOutput, usedTypes = [] }) => {
    const [form, setForm] = useState({
        key: initialData.key || "",
        label: initialData.label || "",
        type: initialData.type || "text",
        placeholder: initialData.placeholder || "",
        default_value: initialData.default_value || "",
        options: initialData.options ? JSON.stringify(initialData.options) : "",
        is_required: initialData.is_required !== false,
        is_hidden: initialData.is_hidden || false,
        sort_order: initialData.sort_order || 0
    });

    const [uploadingPreview, setUploadingPreview] = useState(false);

    const handlePreviewUpload = async (e) => {
        try {
            const file = e.target.files[0];
            if (!file) return;
            setUploadingPreview(true);

            // Sanitize
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
            const fileName = `preview_${Date.now()}_${sanitizedName}`;
            const filePath = `audio-previews/${fileName}`;

            const { error: uploadError } = await supabase.storage.from("assets").upload(filePath, file);
            if (uploadError) throw uploadError;

            const { data } = supabase.storage.from("assets").getPublicUrl(filePath);
            if (data?.publicUrl) {
                setForm(prev => ({ ...prev, default_value: data.publicUrl }));
            }
        } catch (err) {
            console.error(err);
            alert("Gagal upload preview audio: " + err.message);
        } finally {
            setUploadingPreview(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        let optionsJson = null;
        if (form.options) {
            try { optionsJson = JSON.parse(form.options); }
            catch { alert("Invalid JSON for Options"); return; }
        }
        onSave({ ...initialData, ...form, options: optionsJson });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-w-lg mx-auto">
            <h3 className="text-lg font-bold text-slate-800 mb-2">{initialData.id ? "Edit Field" : "Buat Field Baru"}</h3>

            {/* MAGIC PROMPT INFO - DYNAMIC */}
            {featureOutput !== 'chatbox' && (
                <div className="bg-sky-50 border border-sky-100 p-3 rounded-lg mb-4 text-xs text-sky-700 transition-all">
                    <p className="font-bold flex items-center gap-1.5 mb-1"><Icons.Sparkles size={14} />Tips:</p>
                    <ul className="list-disc list-inside space-y-0.5 ml-1 opacity-90">
                        {/* TIPS FOR IMAGE */}
                        {(form.type === 'image' || form.type === 'file_multi') && (
                            <>
                                <li>Gunakan Key n8n <b>image_url</b> untuk <b>Start Image</b> (Magic Prompt Aktif).</li>
                                <li>Gunakan Key n8n <b>image_url_2</b> untuk <b>End Image</b>.</li>
                                <li>Gunakan Key n8n <b>Prompt</b> untuk <b>Mengaktifkan Magic Prompt</b>.</li>
                            </>
                        )}

                        {/* TIPS FOR TEXT/TEXTAREA */}
                        {(form.type === 'text' || form.type === 'textarea') && (
                            <>
                                <li>Gunakan Key n8n <b>image_url</b> untuk <b>Start Image</b> (Magic Prompt Aktif).</li>
                                <li>Gunakan Key n8n <b>image_url_2</b> untuk <b>End Image</b>.</li>
                                <li>Gunakan Key n8n <b>Prompt</b> untuk <b>Mengaktifkan Magic Prompt</b>.</li>
                            </>
                        )}

                        {/* TIPS FOR SLIDER */} {/* UPDATED: Generic Multiplier Explanation */}
                        {form.type === 'slider' && (
                            <>
                                <li>Key <b>jumlah_gambar</b>: Aktifkan <b>Credit Multiplier</b> (Biaya = Harga x Slider). Cocok untuk output banyak file (Gambar/Video/Dokumen/Dan lain lain).</li>
                                <li>Key lain: Slider angka biasa (Tidak mempengaruhi harga kredit).</li>
                            </>
                        )}

                        {/* FALLBACK IF NONE */}
                        {!['image', 'file_multi', 'text', 'textarea', 'slider'].includes(form.type) && (
                            <li>Pastikan <b>JSON Key</b> sesuai dengan parameter yang dibutuhkan N8N.</li>
                        )}
                    </ul>
                </div>
            )}

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Tipe Input</label>
                    <select className="w-full border rounded px-3 py-2 text-sm" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                        {featureOutput === 'chatbox' ? (
                            <>
                                <option value="textarea" disabled={usedTypes.includes('textarea') && initialData.type !== 'textarea'}>
                                    Text Area {usedTypes.includes('textarea') && initialData.type !== 'textarea' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="file_multi" disabled={usedTypes.includes('file_multi') && initialData.type !== 'file_multi'}>
                                    Upload Multi File {usedTypes.includes('file_multi') && initialData.type !== 'file_multi' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="image" disabled={usedTypes.includes('image') && initialData.type !== 'image'}>
                                    Upload Gambar {usedTypes.includes('image') && initialData.type !== 'image' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="pdf" disabled={usedTypes.includes('pdf') && initialData.type !== 'pdf'}>
                                    Upload PDF (PDF) {usedTypes.includes('pdf') && initialData.type !== 'pdf' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="docx" disabled={usedTypes.includes('docx') && initialData.type !== 'docx'}>
                                    Upload Docx (Word) {usedTypes.includes('docx') && initialData.type !== 'docx' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="excel" disabled={usedTypes.includes('excel') && initialData.type !== 'excel'}>
                                    Upload Excel (XLSX) {usedTypes.includes('excel') && initialData.type !== 'excel' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="txt" disabled={usedTypes.includes('txt') && initialData.type !== 'txt'}>
                                    Upload Text (TXT) {usedTypes.includes('txt') && initialData.type !== 'txt' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="audio" disabled={usedTypes.includes('audio') && initialData.type !== 'audio'}>
                                    Upload Audio {usedTypes.includes('audio') && initialData.type !== 'audio' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="video" disabled={usedTypes.includes('video') && initialData.type !== 'video'}>
                                    Upload Video (MP4) {usedTypes.includes('video') && initialData.type !== 'video' ? '(Sudah Ada)' : ''}
                                </option>
                                <option value="choice_audio">
                                    Pilihan Audio (Preview)
                                </option>
                                {!['textarea', 'file_multi', 'image', 'pdf', 'docx', 'excel', 'txt', 'audio', 'video', 'choice_audio'].includes(form.type) && (
                                    <option value={form.type}>{form.type} (Saat Ini / Tidak Valid)</option>
                                )}
                            </>
                        ) : (
                            <>
                                <option value="text">Input Text</option>
                                <option value="textarea">Text Area</option>
                                <option value="number">Angka (Number)</option>
                                <option value="dropdown">Pilihan (Dropdown)</option>
                                <option value="button_group">Pilihan (Button)</option>
                                <option value="slider">Slider (Angka)</option>
                                <option value="file_multi">Upload Multi File</option>
                                <option value="image">Upload Gambar</option>
                                <option value="video">Upload Video (MP4)</option>
                                <option value="pdf">Upload PDF (PDF)</option>
                                <option value="docx">Upload Docx (Word)</option>
                                <option value="excel">Upload Excel (XLSX)</option>
                                <option value="txt">Upload Text (TXT)</option>
                                <option value="audio">Upload Audio</option>
                                <option value="choice_audio">Pilihan Audio (Preview)</option>
                            </>
                        )}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Key N8N</label>
                    {form.type === 'textarea' && featureOutput === 'chatbox' ? (
                        <select
                            className="w-full border rounded px-3 py-2 text-sm bg-slate-50 font-mono"
                            required
                            value={form.key}
                            onChange={e => setForm({ ...form, key: e.target.value })}
                        >
                            <option value="">-- Pilih Key --</option>
                            <option value="prompt">prompt (Input Utama)</option>
                        </select>
                    ) : (
                        <input className="w-full border rounded px-3 py-2 text-sm font-mono" required value={form.key} onChange={e => setForm({ ...form, key: e.target.value })} placeholder="contoh: prompt" />
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Label (Tampilan)</label>
                    <input className="w-full border rounded px-3 py-2 text-sm" required value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Contoh: Masukkan Prompt" />
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Urutan</label>
                    <input type="number" className="w-full border rounded px-3 py-2 text-sm" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: e.target.value })} />
                </div>
            </div>

            {form.key === 'image' && form.type !== 'image' && form.type !== 'file_multi' && (
                <div className="mt-2 bg-amber-50 border border-amber-200 p-2 rounded flex items-center gap-2 text-xs text-amber-800">
                    <Icons.AlertTriangle size={14} className="shrink-0" />
                    <span><b>Warning:</b> Key <b>image</b> terdeteksi tapi Tipe Input adalah <b>{form.type}</b>. Disarankan ubah ke <b>Upload Gambar</b>.</span>
                </div>
            )}

            {(form.type === 'dropdown' || form.type === 'button_group') && (
                <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Pilihan Options (JSON Array)</label>
                    <textarea
                        className="w-full border rounded px-3 py-2 text-xs font-mono bg-slate-50"
                        rows="3"
                        value={form.options || '[{"label":"Gaya A","value":"a"}, {"label":"Gaya B","value":"b"}]'}
                        onChange={e => setForm({ ...form, options: e.target.value })}
                        placeholder="Masukkan JSON array..."
                    />
                </div>
            )}



            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Placeholder</label>
                    <input className="w-full border rounded px-3 py-2 text-sm" value={form.placeholder} onChange={e => setForm({ ...form, placeholder: e.target.value })} />
                </div>

                {/* Unified Limit / Max Size Input */}
                {['image', 'file', 'pdf', 'docx', 'excel', 'txt', 'audio', 'video'].includes(form.type) ? (
                    <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Maksimal Ukuran File (MB)</label>
                        <input
                            type="number"
                            className="w-full border rounded px-3 py-2 text-sm"
                            placeholder="Contoh: 10"
                            value={(() => {
                                try { const o = JSON.parse(form.options || '{}'); return o.max_size_mb || ""; }
                                catch { return ""; }
                            })()}
                            onChange={e => {
                                const val = e.target.value;
                                try {
                                    const o = JSON.parse(form.options || '{}');
                                    if (val) o.max_size_mb = parseInt(val);
                                    else delete o.max_size_mb;
                                    setForm({ ...form, options: JSON.stringify(o) });
                                } catch {
                                    setForm({ ...form, options: JSON.stringify({ max_size_mb: parseInt(val) }) });
                                }
                            }}
                        />
                    </div>
                ) : (
                    <div>
                        <label className="block text-xs font-bold text-slate-600 uppercase mb-1">
                            {(form.type === 'dropdown' || form.type === 'button_group' || form.type === 'choice_button') ? 'Kustom' :
                                form.type === 'slider' ? 'Nilai Maksimum (Max Value)' :
                                    form.type === 'file_multi' ? 'Maksimal Jumlah File (Limit)' :
                                        (form.type === 'textarea' || form.type === 'text') ? 'Maksimal Panjang Karakter' :
                                            form.type === 'choice_audio' ? 'File Preview Audio (Contoh)' :
                                                'Nilai Default'}
                        </label>
                        {(form.type === 'dropdown' || form.type === 'button_group' || form.type === 'choice_button') ? (
                            <select
                                className="w-full border rounded px-3 py-2 text-sm bg-white"
                                value={form.default_value}
                                onChange={e => setForm({ ...form, default_value: e.target.value })}
                            >
                                <option value="">-- Pilih --</option>
                                <option value="active">Aktif</option>
                                <option value="inactive">Tidak Aktif</option>
                            </select>
                        ) : form.type === 'choice_audio' ? (
                            <div className="flex flex-col gap-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="file"
                                        accept="audio/*" // Accept audio
                                        className="block w-full text-xs text-slate-500 file:mr-2 file:py-1 file:px-2 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        disabled={uploadingPreview}
                                        onChange={handlePreviewUpload}
                                    />
                                    {uploadingPreview && <Icons.Loader2 size={16} className="animate-spin text-blue-500" />}
                                </div>
                                {form.default_value && (
                                    <div className="p-2 border rounded-lg bg-slate-50 flex items-center justify-between gap-2 overflow-hidden">
                                        <div className="flex items-center gap-2 truncate">
                                            <Icons.Music size={14} className="text-slate-400 shrink-0" />
                                            <a href={form.default_value} target="_blank" rel="noreferrer" className="text-[10px] text-blue-600 truncate hover:underline">
                                                {form.default_value.split('/').pop()}
                                            </a>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setForm({ ...form, default_value: "" })}
                                            className="text-slate-400 hover:text-rose-500"
                                        >
                                            <Icons.X size={14} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <input className="w-full border rounded px-3 py-2 text-sm" value={form.default_value} onChange={e => setForm({ ...form, default_value: e.target.value })} placeholder={form.type === 'slider' ? "Contoh: 10" : form.type === 'file_multi' ? "Contoh: 5" : (form.type === 'textarea' || form.type === 'text') ? "Contoh: 1000" : ""} />
                        )}
                    </div>
                )}
            </div>

            <div className="pt-2 flex gap-4">
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.is_required} onChange={e => setForm({ ...form, is_required: e.target.checked })} />
                    <span className="text-sm text-slate-700">Wajib Diisi (Required)</span>
                </label>
                <label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.is_hidden} onChange={e => setForm({ ...form, is_hidden: e.target.checked })} />
                    <span className="text-sm text-slate-700">Sembunyikan (Hidden)</span>
                </label>
            </div>

            <div className="pt-6 border-t flex items-center justify-between">
                {initialData.id ? (
                    <button type="button" onClick={() => onDelete(initialData.id)} className="text-rose-600 text-sm hover:underline">Hapus Field</button>
                ) : <div></div>}
                <div className="flex gap-2">
                    <button type="button" onClick={onCancel} className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded text-sm">Batal</button>
                    <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded text-sm font-bold hover:bg-blue-700">Simpan Field</button>
                </div>
            </div>
        </form>
    );
};


// Sub-component: Copyable Input Style (Matches Screenshot)
const CopyableInput = ({ label, code, typeBadge, fullWidth = false, color = "blue" }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const colorClasses = {
        blue: { border: "border-blue-100", bg: "bg-blue-50/30", text: "text-blue-600", hover: "hover:bg-blue-50", badge: "bg-slate-200 text-slate-600" },
        purple: { border: "border-purple-100", bg: "bg-purple-50/30", text: "text-purple-600", hover: "hover:bg-purple-50", badge: "bg-purple-100 text-purple-600" },
    };
    const c = colorClasses[color] || colorClasses.blue;

    return (
        <div className={`border ${fullWidth ? "w-full" : ""} border-slate-200 rounded-lg p-3 bg-white`}>
            <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-bold text-slate-700">{label}</span>
                {typeBadge && <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded font-bold ${c.badge}`}>{typeBadge}</span>}
            </div>
            <div className="relative group cursor-pointer" onClick={handleCopy}>
                <div className={`w-full border ${c.border} rounded ${c.bg} px-3 py-2 text-xs font-mono ${c.text} break-all pr-8 ${c.hover} transition-colors`}>
                    {code}
                </div>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
                    {copied ? (
                        <Icons.Check size={14} className="text-emerald-500" />
                    ) : (
                        <Icons.Copy size={14} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                    )}
                </div>
                {copied && <div className="absolute right-8 top-1/2 -translate-y-1/2 text-[10px] text-emerald-600 font-bold animate-in fade-in slide-in-from-right-1">Tersalin!</div>}
            </div>
        </div>
    );
};

// Sub-component: Simple Copy Row (For Switch)
const SimpleCopyRow = ({ label, code, valueLabel }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="flex items-center border rounded-md overflow-hidden text-xs">
            <div className="bg-slate-50 px-3 py-2 text-slate-500 font-medium w-40 shrink-0 border-r">{label}</div>
            <div className="flex-1 px-3 py-2 font-mono text-slate-700 bg-white truncate flex justify-between items-center group cursor-pointer hover:bg-slate-50 transition-colors" onClick={handleCopy}>
                <span className="text-purple-600">{code}</span>
                <span className='ml-2'>
                    {copied ? <Icons.Check size={12} className="text-emerald-500" /> : <Icons.Copy size={12} className="text-slate-300 opacity-0 group-hover:opacity-100" />}
                </span>
            </div>
        </div>
    );
};


// Sub-component: N8n Guide Model
const N8nGuideModal = ({ model, fields, feature, onClose }) => {
    // Determine the primary output type
    const isBinaryOutput = ['image', 'audio', 'video', 'file'].includes(feature?.output_type);

    // Download Guide Handler
    const handleDownloadGuide = () => {
        const title = `INTEGRATION GUIDE: ${model.name.toUpperCase()}`;
        const webhookUrl = model.n8n_webhook_path || model.feature_webhook_route || "[WEBHOOK URL NOT SET]";
        const isBinaryOutput = ['image', 'audio', 'video', 'file'].includes(feature?.output_type);

        // Input Fields Text
        const inputFieldsText = fields && fields.length > 0
            ? fields.map(f => `   • ${f.label} (${f.key}) -> {{ $json.body["${f.key}"] }}`).join('\n')
            : "   (No input fields configured)";

        // Optional Configs Text
        const configData = model.configuration_options;
        const configCategories = Array.isArray(configData) ? configData : (configData?.categories || []);
        const configKeys = [...new Set(configCategories.flatMap(c => c.target_keys || []))];
        const configText = configKeys.length > 0
            ? configKeys.map(k => `   • ${k} -> {{ $json.body["${k}"] }}`).join('\n')
            : "   (No optional configuration)";

        const content = `
${title}
${"=".repeat(title.length)}

1. WEBHOOK CONFIGURATION
------------------------
Method:   POST
URL:      ${webhookUrl}

2. SWITCH LOGIC (ROUTING)
-------------------------
Rule:     {{ $json.body._model_key }}
Operator: Equal to
Value:    ${model.key}

3. INPUT PARSING (JSON)
-----------------------
Map the following inputs in your N8N workflow:

[User Inputs]
${inputFieldsText}

[Advanced Configuration]
${configText}

4. OUTPUT RESPONSE
------------------
Node: "Respond to Webhook"
Type: ${isBinaryOutput ? "BINARY (File)" : "JSON (Text)"}

${isBinaryOutput ?
                `[Settings]
   • Respond With:  Binary
   • Property Name: data

[Expected Format: ${(() => {
                    const formats = {
                        'image': 'jpg, png, gif, webp',
                        'video': 'mp4, mpeg, webm, mov, mkv',
                        'audio': 'mp3, wav, ogg, m4a, flac, aac',
                        'file': 'pdf, doc, docx, txt, csv'
                    };
                    let text = formats[feature?.output_type] || 'Any binary file';

                    // NEW CHECK: If "jumlah_gambar" exists, warn about ZIP
                    const hasMultiImg = fields?.some(f => f.key === 'jumlah_gambar');
                    if (hasMultiImg) {
                        text += '\n    • NOTE: Jika hasil lebih dari 1 file, WAJIB output .ZIP agar semua file terbaca.';
                    }
                    return text;
                })()}]`
                :
                `[Settings]
   • Respond With:  JSON
   • Response Body: {{ { "result": $json.content } }}

[Expected Format: JSON, Markdown, HTML, Plain Text]`}


5. CALLBACK / FINAL RESPONSE (IMPORTANT)
----------------------------------------
Because we use ASYNC processing to avoid timeouts, you MUST send the result back manually.

Node:     HTTP Request
Method:   POST
URL:      https://zarwmajsgjyymvehvgco.supabase.co/functions/v1/webhook-result
Authentication: None

[Send Query Parameters: ON]
   • Mode: Using Fields Below
   • Name: run_id   -> Value: {{ $node["Webhook"].json.body._run_id }}   (Note: Ganti "Webhook" sesuai nama node Anda)
   • Name: status   -> Value: success

[Send Body: ON]
   • Body Content Type: n8n Binary File
   • Input Data Field Name: data (Note: Sesuaikan dengan property binary node sebelumnya, misal "zip")

==================================================
Generated by NeoAI | ${new Date().toISOString().split('T')[0]}
==================================================
`.trim();

        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Guide_${model.key}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col font-sans">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white sticky top-0 z-10 rounded-t-xl">
                    <h2 className="text-lg font-bold text-slate-800">Panduan Integrasi N8N</h2>
                    <button onClick={onClose}><Icons.X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
                </div>

                <div className="p-6 overflow-y-auto space-y-8 scrollbar-thin bg-white">

                    {/* 1. Webhook Setup */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">1</div>
                            <h3 className="text-sm font-bold text-slate-800">Konfigurasi Webhook</h3>
                        </div>
                        <div className="ml-9">
                            <p className="text-xs text-slate-500 mb-3">Buat node <span className="font-mono border px-1 rounded bg-slate-50 text-slate-600">Webhook</span> di N8n. Set Method ke <b>POST</b>.</p>
                            <div className="bg-slate-800 text-slate-200 rounded-lg p-4 font-mono text-xs flex justify-between items-center group relative cursor-pointer hover:bg-slate-900 transition-colors"
                                onClick={() => { navigator.clipboard.writeText(model.n8n_webhook_path || model.feature_webhook_route); alert("Tersalin!"); }}>
                                <span className="break-all mr-4">{model.n8n_webhook_path || model.feature_webhook_route || "⚠️ Tidak ada Webhook Path"}</span>
                                <Icons.Copy className="w-4 h-4 text-slate-500 group-hover:text-white transition-colors" />
                            </div>
                        </div>
                    </div>

                    {/* 2. Logika Percabangan */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">2</div>
                            <h3 className="text-sm font-bold text-slate-800">Logika Percabangan (Switch Node)</h3>
                        </div>
                        <div className="ml-9">
                            <p className="text-xs text-slate-500 mb-3">Jika satu workflow menangani banyak model, gunakan node <b>Switch</b> untuk memilah logika.</p>

                            <div className="bg-slate-50/50 border border-slate-200 rounded-lg p-4">
                                <h4 className="text-xs font-bold text-slate-700 mb-3">Setup Switch Rule:</h4>
                                <div className="space-y-2 bg-white rounded border border-slate-100 p-1">
                                    <SimpleCopyRow label="Expression (Value 1):" code="{{ $json.body._model_key }}" />
                                    <div className="flex items-center border rounded-md overflow-hidden text-xs">
                                        <div className="bg-slate-50 px-3 py-2 text-slate-500 font-medium w-40 shrink-0 border-r">Operator:</div>
                                        <div className="flex-1 px-3 py-2 font-bold text-slate-800 bg-white">Equal to</div>
                                    </div>
                                    <SimpleCopyRow label="Value 2 (Model Key ini):" code={model.key} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 3. Input Mapping */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">3</div>
                            <h3 className="text-sm font-bold text-slate-800">Mengambil Input User</h3>
                        </div>
                        <div className="ml-9">
                            <p className="text-xs text-slate-500 mb-4">Gunakan ekspresi berikut di node n8n untuk membaca data yang dikirim user:</p>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {fields && fields.length > 0 ? fields.map(f => (
                                    <CopyableInput
                                        key={f.id}
                                        label={f.label}
                                        typeBadge={f.type}
                                        code={`{{ $json.body["${f.key}"] }}`}
                                        color="blue"
                                    />
                                )) : (
                                    <p className="text-xs text-slate-400 italic">Belum ada input field.</p>
                                )}
                            </div>

                            {/* Optional Configs - PURPLE */}
                            {(() => {
                                const configData = model.configuration_options;
                                const configCategories = Array.isArray(configData) ? configData : (configData?.categories || []);
                                const configKeys = [...new Set(configCategories.flatMap(c => c.target_keys || []))];
                                if (configKeys.length > 0) {
                                    return (
                                        <div className="mt-6 pt-4 border-t border-slate-100">
                                            <h4 className="text-xs font-bold text-purple-600 uppercase mb-3 text-center flex items-center justify-center gap-2">
                                                <Icons.Settings2 size={14} /> Model Options (Opsional)
                                            </h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {configKeys.map(key => (
                                                    <CopyableInput
                                                        key={key}
                                                        label={key}
                                                        typeBadge="OPTION"
                                                        code={`{{ $json.body["${key}"] }}`}
                                                        color="purple"
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    </div>

                    {/* 4. Output Configuration */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">4</div>
                            <h3 className="text-sm font-bold text-slate-800">Mengembalikan Output (Response)</h3>
                        </div>
                        <div className="ml-9">
                            <p className="text-xs text-slate-500 mb-3">Di akhir workflow, gunakan node <span className="font-mono border px-1 rounded bg-slate-50 text-slate-700">Respond to Webhook</span>.</p>

                            <div className={`border rounded-lg p-5 ${isBinaryOutput ? 'bg-indigo-50/30 border-indigo-100' : 'bg-emerald-50/30 border-emerald-100'}`}>
                                <h4 className={`text-sm font-bold mb-4 flex items-center gap-2 ${isBinaryOutput ? 'text-indigo-700' : 'text-emerald-700'}`}>
                                    {isBinaryOutput ? <Icons.Image size={16} /> : <Icons.AlignLeft size={16} />}
                                    {(() => {
                                        const outputType = feature?.output_type || 'text';
                                        const labels = {
                                            'image': 'Return Image (Binary File)',
                                            'video': 'Return Video (Binary File)',
                                            'audio': 'Return Audio (Binary File)',
                                            'file': 'Return Document/File (Binary)',
                                            'text': 'Return JSON (Text)'
                                        };
                                        return labels[outputType] || 'Return File (Binary)';
                                    })()}
                                </h4>

                                {isBinaryOutput ? (
                                    <div className="space-y-4">
                                        <div className="text-xs text-indigo-900 bg-indigo-50 p-3 rounded">
                                            Karena output fitur ini adalah <b className="uppercase">{feature.output_type}</b>, kembalikan response sebagai <b>Binary</b>.

                                            {/* Format Requirements Table */}
                                            <div className="mt-3 bg-white rounded border border-indigo-100 overflow-hidden">
                                                <div className="bg-indigo-100/50 px-3 py-1.5 text-[10px] font-bold text-indigo-800 uppercase">
                                                    Format yang Diterima
                                                </div>
                                                <div className="p-2 text-[11px] space-y-1">
                                                    {(() => {
                                                        const formats = {
                                                            'image': 'jpg, png, gif, webp',
                                                            'video': 'mp4, mpeg, webm, mov, mkv',
                                                            'audio': 'mp3, wav, ogg, m4a, flac, aac',
                                                            'file': 'pdf, doc, docx, txt, csv'
                                                        };
                                                        const format = formats[feature.output_type] || 'Any binary file';
                                                        return <div className="font-mono text-indigo-700 font-semibold">{format}</div>;
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded border border-indigo-100">
                                            <SimpleCopyRow label="Respond With" code="Binary" />
                                            <SimpleCopyRow label="Property Name" code="data" />
                                        </div>

                                        {/* NEW: Warning for Multi-Image ZIP */}
                                        {fields?.some(f => f.key === 'jumlah_gambar') && (
                                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-start gap-2">
                                                <Icons.AlertTriangle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
                                                <div>
                                                    <span className="font-bold">PENTING:</span> Jika hasil generate lebih dari 1 file (sesuai input <b>jumlah_gambar</b>), WAJIB kembalikan response sebagai file <b>.ZIP</b>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="text-xs text-emerald-900 bg-emerald-50 p-3 rounded">
                                            Karena output fitur ini adalah <b>TEXT</b>, kembalikan response sebagai <b>JSON</b>.

                                            {/* Format Requirements */}
                                            <div className="mt-3 bg-white rounded border border-emerald-100 overflow-hidden">
                                                <div className="bg-emerald-100/50 px-3 py-1.5 text-[10px] font-bold text-emerald-800 uppercase">
                                                    Format yang Diterima
                                                </div>
                                                <div className="p-2 text-[11px]">
                                                    <div className="font-mono text-emerald-700 font-semibold">JSON, Markdown, HTML, Plain Text</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="bg-white rounded border border-emerald-100">
                                            <SimpleCopyRow label="Respond With" code="JSON" />
                                            <div className="border-t border-slate-100">
                                                <div className="text-[10px] text-slate-400 px-3 py-1 font-mono uppercase bg-slate-50">Response Body Expression:</div>
                                                <div className="p-3 font-mono text-xs text-emerald-700 bg-white cursor-pointer hover:bg-emerald-50/30 transition-colors" onClick={() => { navigator.clipboard.writeText('{{ { "result": $json.content.parts[0].text } }}'); alert('Tersalin!'); }}>
                                                    {`{{ { "result": $json.content.parts[0].text } }}`}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>



                    {/* 5. Callback Configuration */}
                    <div className="space-y-2">
                        <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold">5</div>
                            <h3 className="text-sm font-bold text-slate-800">Callback Result (Wajib)</h3>
                        </div>
                        <div className="ml-9">
                            <p className="text-xs text-slate-500 mb-3">Karena proses di-handle secara background (Async), N8n <b>WAJIB</b> mengirimkan hasil balik ke server via HTTP Request.</p>


                            {/* Detailed N8N Configuration Table */}
                            <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden">
                                <div className="bg-slate-100 px-4 py-2 border-b border-slate-200 flex justify-between items-center">
                                    <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">HTTP Request Node Settings</h4>
                                    <span className="text-[10px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded-full font-bold">Parameters Tab</span>
                                </div>

                                <div className="divide-y divide-slate-100 text-xs">
                                    {/* Method & URL */}
                                    <div className="p-3 grid grid-cols-[140px_1fr] gap-4 items-center">
                                        <div className="font-bold text-slate-600">Method</div>
                                        <div className="font-mono font-bold text-blue-700 bg-blue-50 inline-block px-2 py-0.5 rounded w-max border border-blue-100">POST</div>
                                    </div>
                                    <div className="p-3 grid grid-cols-[140px_1fr] gap-4 items-center">
                                        <div className="font-bold text-slate-600">URL</div>
                                        <CopyableInput fullWidth code="https://zarwmajsgjyymvehvgco.supabase.co/functions/v1/webhook-result" color="blue" />
                                    </div>
                                    <div className="p-3 grid grid-cols-[140px_1fr] gap-4 items-center">
                                        <div className="font-bold text-slate-600">Authentication</div>
                                        <div className="font-medium text-slate-500">None</div>
                                    </div>

                                    {/* Query Parameters */}
                                    <div className="p-3 bg-slate-50/50">
                                        <div className="grid grid-cols-[140px_1fr] gap-4 mb-2">
                                            <div className="font-bold text-slate-600 flex items-center gap-2">
                                                Send Query Params
                                                <div className="w-6 h-3 bg-emerald-500 rounded-full relative"><div className="w-2 h-2 bg-white rounded-full absolute right-0.5 top-0.5"></div></div>
                                            </div>
                                            <div className="text-emerald-600 font-bold text-[10px] uppercase">ON</div>
                                        </div>

                                        <div className="ml-[140px] space-y-2 border-l-2 border-emerald-100 pl-3">
                                            <div className="grid grid-cols-[60px_1fr] gap-2 items-center">
                                                <span className="text-[10px] font-bold text-slate-500 text-right">Name:</span>
                                                <div className="font-mono text-slate-700 bg-white border border-slate-200 px-2 py-1 rounded">run_id</div>

                                                <span className="text-[10px] font-bold text-slate-500 text-right">Value:</span>
                                                <div className="flex flex-col gap-1">
                                                    <CopyableInput code={`{{ $node["Webhook"].json.body._run_id }}`} color="purple" typeBadge="EXPRESSION" />
                                                    <span className="text-[10px] text-amber-600 font-medium italic">*Ganti "Webhook" dengan nama node webhook Anda.</span>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-[60px_1fr] gap-2 items-center">
                                                <span className="text-[10px] font-bold text-slate-500 text-right">Name:</span>
                                                <div className="font-mono text-slate-700 bg-white border border-slate-200 px-2 py-1 rounded">status</div>

                                                <span className="text-[10px] font-bold text-slate-500 text-right">Value:</span>
                                                <div className="font-mono text-slate-700 bg-white border border-slate-200 px-2 py-1 rounded">success</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Body */}
                                    <div className="p-3 bg-slate-50/50">
                                        <div className="grid grid-cols-[140px_1fr] gap-4 mb-2">
                                            <div className="font-bold text-slate-600 flex items-center gap-2">
                                                Send Body
                                                <div className="w-6 h-3 bg-emerald-500 rounded-full relative"><div className="w-2 h-2 bg-white rounded-full absolute right-0.5 top-0.5"></div></div>
                                            </div>
                                            <div className="text-emerald-600 font-bold text-[10px] uppercase">ON</div>
                                        </div>

                                        <div className="ml-[140px] space-y-2 border-l-2 border-emerald-100 pl-3">
                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-center">
                                                <span className="text-[10px] font-bold text-slate-500">Body Content Type:</span>
                                                <div className="font-bold text-slate-700">n8n Binary File</div>
                                            </div>
                                            <div className="grid grid-cols-[120px_1fr] gap-2 items-center">
                                                <span className="text-[10px] font-bold text-slate-500">Input Data Field:</span>
                                                <div className="flex flex-col gap-1">
                                                    <div className="font-mono text-slate-700 bg-white border border-slate-200 px-2 py-1 rounded w-max">data</div>
                                                    <span className="text-[10px] text-amber-600 font-medium italic">*Sesuaikan dengan property binary node sebelumnya (misal "zip").</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
                <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
                    <button onClick={handleDownloadGuide} className="px-4 py-2 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-bold transition-all flex items-center gap-2 border border-transparent hover:border-blue-100">
                        <Icons.Download className="w-4 h-4" /> Download Guide (.txt)
                    </button>
                    <button onClick={onClose} className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-md hover:bg-blue-700 hover:shadow-lg transition-all">Saya Sudah Setup N8n</button>
                </div>
            </div>
        </div >
    );
};

// Sub-component: External Buttons Manager
const ExternalButtonsModal = ({ initialButtons, onSave, onClose }) => {
    // Buttons: [{ name, url, description, banner_image, banner_link, button_color }]
    const [buttons, setButtons] = useState(initialButtons || []);
    const [uploading, setUploading] = useState(false);

    const handleChange = (index, field, value) => {
        const newButtons = [...buttons];
        newButtons[index][field] = value;
        setButtons(newButtons);
    };

    const handleAdd = () => {
        if (buttons.length >= 2) return;
        setButtons([...buttons, { name: "", url: "", description: "", banner_image: "", banner_link: "", button_color: "#2563eb" }]);
    };

    const handleRemove = (index) => {
        const newButtons = [...buttons];
        newButtons.splice(index, 1);
        setButtons(newButtons);
    };

    const handleFileUpload = async (index, e) => {
        try {
            const file = e.target.files[0];
            if (!file) return;

            setUploading(true);

            // Keep original name but sanitize slightly for safety
            const sanitizedName = file.name.replace(/[^a-zA-Z0-9.\-_]/g, '_');
            const filePath = `button-assets/${sanitizedName}`;

            // Check if exists
            const { data: existingFiles } = await supabase.storage.from("assets").list("button-assets", {
                search: sanitizedName,
                limit: 1
            });

            const exists = existingFiles && existingFiles.find(f => f.name === sanitizedName);

            if (exists) {
                // Skip upload, use existing
                console.log("File exists, skipping upload:", filePath);
                handleChange(index, 'banner_image', filePath);
                alert("File dengan nama yang sama ditemukan. Menggunakan file yang sudah ada (tidak upload ulang).");
                return;
            }

            const { error: uploadError } = await supabase.storage
                .from('assets')
                .upload(filePath, file);

            if (uploadError) {
                // Handle race condition
                if (uploadError.statusCode === '409' || uploadError.message.includes('Duplicate')) {
                    handleChange(index, 'banner_image', filePath);
                    return;
                }
                throw uploadError;
            }

            // Update button data
            handleChange(index, 'banner_image', filePath);
        } catch (error) {
            alert('Error uploading image: ' + error.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="text-lg font-bold text-slate-800">Kelola Tombol Eksternal</h2>
                    <button onClick={onClose}><Icons.X className="w-5 h-5 text-slate-400" /></button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto flex-1">
                    <div className="bg-blue-50 text-blue-700 border border-blue-100 p-3 rounded-lg text-xs">
                        Tombol ini memiliki 2 Mode:
                        <ul className="list-disc list-inside mt-1 ml-1 font-bold">
                            <li>Mode Banner: Tampil GAMBAR sebelum user generate. (Link Banner)</li>
                            <li>Mode Tombol: Tampil TOMBOL setelah user berhasil generate. (Link Tombol)</li>
                            <li>Mode Tombol: Sisakan value warna tombol # untuk tidak menampakkannya</li>
                        </ul>
                    </div>

                    <div className="space-y-6">
                        {buttons.map((btn, idx) => (
                            <div key={idx} className="border border-slate-200 rounded-xl p-4 relative bg-slate-50/50">
                                <button onClick={() => handleRemove(idx)} className="absolute top-2 right-2 text-rose-500 hover:text-rose-700 p-1 bg-white rounded-full border border-slate-200 hover:bg-rose-50"><Icons.Trash2 size={16} /></button>
                                <h3 className="text-xs font-bold text-slate-500 uppercase mb-3 flex items-center gap-2">
                                    <span className="bg-slate-200 px-1.5 py-0.5 rounded text-slate-600">#{idx + 1}</span>
                                    Konfigurasi Tombol
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    {/* Left: General Info */}
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Label Tombol</label>
                                            <input className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Contoh: Tutorial Youtube" value={btn.name} onChange={e => handleChange(idx, 'name', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Deskripsi Singkat</label>
                                            <input className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Penjelasan singkat..." value={btn.description} onChange={e => handleChange(idx, 'description', e.target.value)} />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-600 uppercase mb-1">Warna Tombol</label>
                                            <div className="flex gap-2">
                                                <input type="color" className="h-9 w-9 p-0.5 rounded border cursor-pointer shrink-0" value={btn.button_color || "#2563eb"} onChange={e => handleChange(idx, 'button_color', e.target.value)} />
                                                <input className="w-full border rounded px-2 py-1.5 text-sm font-mono uppercase" placeholder="#..." value={btn.button_color || "#2563eb"} onChange={e => handleChange(idx, 'button_color', e.target.value)} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right: Banner Image */}
                                    <div className="bg-white p-3 rounded-lg border border-slate-200">
                                        <label className="block text-[10px] font-bold text-slate-600 uppercase mb-2">Banner Image (Mode Belum Generate)</label>
                                        <DragDropUploader
                                            accept="image/*"
                                            onUpload={(e) => handleFileUpload(idx, e)}
                                            uploading={uploading}
                                            currentUrl={btn.banner_image}
                                            label="Upload Banner Image"
                                            height="h-16"
                                        />
                                        <p className="mt-2 text-[10px] text-slate-500 italic flex items-center gap-1">
                                            <Icons.Info size={12} className="text-blue-500" />
                                            Masukkan gambar dengan rasio 1100 px x 180 px untuk hasil terbaik
                                        </p>
                                    </div>
                                </div>

                                {/* Links Section */}
                                <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200">
                                    <div>
                                        <label className="block text-[10px] font-bold text-blue-600 uppercase mb-1">🔗 Link Tombol (Diklik saat Selesai Generate)</label>
                                        <input className="w-full border border-blue-200 rounded px-2 py-1.5 text-sm bg-blue-50" placeholder="Contoh: /dashboard/feature/remove-bg" value={btn.url} onChange={e => handleChange(idx, 'url', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-purple-600 uppercase mb-1">🔗 Link Banner (Diklik saat Gambar Muncul)</label>
                                        <input className="w-full border border-purple-200 rounded px-2 py-1.5 text-sm bg-purple-50" placeholder="https://..." value={btn.banner_link || ""} onChange={e => handleChange(idx, 'banner_link', e.target.value)} />
                                    </div>
                                </div>
                                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-100 rounded text-[10px] text-yellow-700">
                                    <strong>💡 Tips Format Link:</strong> Gunakan format <code>/dashboard/feature/slug-fitur</code> (Contoh: <code>/dashboard/feature/remove-bg</code>).
                                    <br />
                                    Format ini memungkinkan <strong>Auto-Fill Gambar</strong> hasil generate ke tool tujuan (Pastikan field tujuan punya key <code>image_url</code>).
                                </div>
                            </div>
                        ))}
                    </div>

                    {buttons.length < 2 ? (
                        <button onClick={handleAdd} className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 text-sm font-medium hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors">
                            + Tambah Tombol
                        </button>
                    ) : (
                        <p className="text-center text-xs text-amber-600 font-medium">Maksimal 2 tombol tercapai.</p>
                    )}

                </div>

                <div className="p-4 border-t bg-slate-50 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">Batal</button>
                    <button onClick={() => onSave(buttons)} disabled={uploading} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-sm hover:bg-blue-700 disabled:opacity-50">Simpan Tombol</button>
                </div>
            </div>
        </div>
    );
};

// Sub-component: Provider Manager Modal
const ProviderManagerModal = ({ onClose, onUpdate }) => {
    const [providers, setProviders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(null); // null or provider obj
    const [form, setForm] = useState({ name: "", icon_url: "" });
    const [uploading, setUploading] = useState({ icon: false });

    useEffect(() => {
        fetchProviders();
    }, []);

    const fetchProviders = async () => {
        setLoading(true);
        const { data } = await supabase.from("ai_model_providers").select("*").order("created_at", { ascending: false });
        setProviders(data || []);
        setLoading(false);
    };

    const handleSave = async () => {
        if (!form.name.trim()) return alert("Name is required");

        try {
            if (editMode) {
                const { error } = await supabase.from("ai_model_providers").update(form).eq("id", editMode.id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from("ai_model_providers").insert(form);
                if (error) throw error;
            }
            setForm({ name: "", icon_url: "" });
            setEditMode(null);
            fetchProviders();
            onUpdate(); // Refresh parent model list if needed (to update dropdown)
        } catch (e) {
            alert("Error saving provider: " + e.message);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm("Delete this provider? Models using it will detach.")) return;
        try {
            const { error } = await supabase.from("ai_model_providers").delete().eq("id", id);
            if (error) throw error;
            fetchProviders();
            onUpdate();
        } catch (e) {
            alert("Error deleting: " + e.message);
        }
    };

    const handleUpload = async (e, type) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(prev => ({ ...prev, [type]: true }));
        try {
            const ext = file.name.split('.').pop();
            const fileName = `provider-${type}-${Date.now()}.${ext}`;
            const path = `providers/${fileName}`;

            const { error } = await supabase.storage.from("assets").upload(path, file);
            if (error) throw error;

            setForm(prev => ({ ...prev, icon_url: path }));
        } catch (err) {
            alert("Upload failed: " + err.message);
        } finally {
            setUploading(prev => ({ ...prev, [type]: false }));
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl overflow-hidden max-h-[90vh] flex flex-col">
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <h2 className="text-lg font-bold text-slate-800">Manage AI Providers</h2>
                    <button onClick={onClose}><Icons.X className="w-5 h-5 text-slate-400" /></button>
                </div>

                <div className="flex-1 flex min-h-0 overflow-hidden">
                    {/* Left: List */}
                    <div className="w-1/3 border-r border-slate-100 overflow-y-auto bg-slate-50 p-3 flex flex-col gap-2">
                        <button
                            onClick={() => { setEditMode(null); setForm({ name: "", icon_url: "" }); }}
                            className={`p-3 rounded-lg text-left text-sm font-bold flex items-center gap-2 border border-dashed hover:bg-white hover:border-blue-300 hover:text-blue-600 transition-all mb-2 ${!editMode ? 'bg-white border-blue-500 text-blue-600 shadow-sm' : 'border-slate-300 text-slate-500'}`}
                        >
                            <Icons.Plus size={16} /> New Provider
                        </button>

                        {loading ? <p className="text-xs text-slate-400 text-center">Loading...</p> : providers.map(p => (
                            <div
                                key={p.id}
                                onClick={() => { setEditMode(p); setForm({ name: p.name, icon_url: p.icon_url || "" }); }}
                                className={`p-3 rounded-lg border group cursor-pointer transition-all ${editMode?.id === p.id ? 'bg-white border-blue-500 shadow-sm ring-1 ring-blue-500/20' : 'bg-white border-slate-200 hover:border-blue-300'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
                                        {p.icon_url ? (
                                            <img src={p.icon_url.startsWith('http') ? p.icon_url : supabase.storage.from('assets').getPublicUrl(p.icon_url).data.publicUrl} className="w-full h-full object-cover rounded" alt="icon" />
                                        ) : <Icons.Box size={14} className="text-slate-400" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-bold text-slate-700 truncate">{p.name}</p>
                                    </div>
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(p.id); }} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded opacity-0 group-hover:opacity-100 transition-all">
                                        <Icons.Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Right: Form */}
                    <div className="flex-1 p-6 overflow-y-auto bg-white">
                        <h3 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
                            {editMode ? <><Icons.Edit3 size={16} className="text-blue-500" /> Edit Provider</> : <><Icons.PlusCircle size={16} className="text-emerald-500" /> Create New Provider</>}
                        </h3>

                        <div className="space-y-4 max-w-lg">
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">Provider Name <span className="text-rose-500">*</span></label>
                                <input
                                    value={form.name}
                                    onChange={e => setForm({ ...form, name: e.target.value })}
                                    className="w-full text-sm border border-slate-300 rounded-lg p-2.5 focus:border-blue-500 outline-none"
                                    placeholder="e.g. Luma Labs"
                                />
                            </div>

                            {/* Icon Upload */}
                            <div>
                                <label className="block text-xs font-bold text-slate-700 mb-1">Icon Logo (Square 1:1)</label>
                                <DragDropUploader
                                    accept="image/*"
                                    onUpload={(e) => handleUpload(e, 'icon')}
                                    uploading={uploading.icon}
                                    currentUrl={form.icon_url}
                                    label="Upload Provider Icon"
                                />
                            </div>

                            <div className="pt-4 flex justify-end gap-2">
                                {editMode && <button onClick={() => { setEditMode(null); setForm({ name: "", icon_url: "" }); }} className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm">Cancel Edit</button>}
                                <button
                                    onClick={handleSave}
                                    disabled={!form.name.trim() || uploading.icon}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-bold shadow-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {editMode ? "Update Provider" : "Create Provider"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminGenericModelsPage;

/* ================= COMPONENT: DUPLICATE TARGET MODAL ================= */
const DuplicateTargetModal = ({ features, currentFeatureId, onClose, onConfirm, modelName }) => {
    const [selectedFeatureId, setSelectedFeatureId] = useState(currentFeatureId);

    const handleConfirm = () => {
        onConfirm(selectedFeatureId);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="font-bold text-slate-800">Duplicate Model</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <Icons.X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="p-3 bg-blue-50 text-blue-700 rounded-md text-sm border border-blue-100">
                        Copying <strong>{modelName}</strong>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-sm font-semibold text-slate-700">Select Target Tool</label>
                        <select
                            value={selectedFeatureId}
                            onChange={(e) => setSelectedFeatureId(e.target.value)}
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        >
                            {features.map(f => (
                                <option key={f.id} value={f.id}>
                                    {f.name} {f.id === currentFeatureId ? "(Current)" : ""}
                                </option>
                            ))}
                        </select>
                        <p className="text-xs text-slate-500">
                            The model will be copied to the selected tool with all its settings and fields.
                        </p>
                    </div>
                </div>

                <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-600/10 transition-all flex items-center gap-1.5"
                    >
                        <Icons.Copy className="w-3.5 h-3.5" />
                        Duplicate
                    </button>
                </div>
            </div>
        </div>
    );
};


/* ================= COMPONENT: DUPLICATE TARGET MODAL (NEW) ================= */
const DuplicateTargetModalNew = ({ features, currentFeatureId, onClose, onConfirm, modelName, isDuplicating }) => {
    const [selectedFeatureId, setSelectedFeatureId] = useState(currentFeatureId);
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");

    const handleConfirm = () => {
        if (!isDuplicating) {
            onConfirm(selectedFeatureId);
        }
    };

    // Ensure type safety for comparison
    const selectedFeature = features.find(f => String(f.id) === String(selectedFeatureId));
    const filteredFeatures = features.filter(f => f.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 pt-10 animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-100 flex justify-between items-center flex-shrink-0 rounded-t-xl">
                    <h3 className="font-bold text-slate-800">Duplicate Model</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <Icons.X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-4 space-y-4 overflow-visible">
                    <div className="p-3 bg-blue-50 text-blue-700 rounded-md text-sm border border-blue-100">
                        Copying <strong>{modelName}</strong>
                    </div>

                    <div className="space-y-1.5 relative">
                        <label className="text-sm font-semibold text-slate-700">Select Target Tool</label>

                        {/* Custom Dropdown Trigger */}
                        <div className="relative">
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-left flex items-center justify-between hover:border-blue-400 focus:ring-2 focus:ring-blue-100 transition-all shadow-sm"
                            >
                                <span className="truncate font-medium text-slate-700">
                                    {selectedFeature ? selectedFeature.name : "Select Feature..."}
                                </span>
                                <Icons.ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                            </button>

                            {/* Dropdown Menu */}
                            {isOpen && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-100">
                                    {/* Search */}
                                    <div className="p-2 border-b border-slate-50 bg-slate-50/50">
                                        <div className="relative">
                                            <Icons.Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                                            <input
                                                autoFocus
                                                type="text"
                                                placeholder="Search tools..."
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200"
                                            />
                                        </div>
                                    </div>

                                    {/* List */}
                                    <div className="max-h-[50vh] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-200 p-1 pb-4">
                                        {filteredFeatures.map(f => (
                                            <button
                                                key={f.id}
                                                onClick={() => {
                                                    setSelectedFeatureId(f.id);
                                                    setIsOpen(false);
                                                    setSearch("");
                                                }}
                                                className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center justify-between group transition-colors ${String(selectedFeatureId) === String(f.id)
                                                    ? 'bg-blue-50 text-blue-700'
                                                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                                    }`}
                                            >
                                                <span className="font-medium">{f.name}</span>
                                                <div className="flex items-center gap-2">
                                                    {String(f.id) === String(currentFeatureId) && (
                                                        <span className="text-[9px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">Current</span>
                                                    )}
                                                    {String(selectedFeatureId) === String(f.id) && <Icons.Check className="w-3.5 h-3.5 text-blue-600" />}
                                                </div>
                                            </button>
                                        ))}
                                        {filteredFeatures.length === 0 && (
                                            <div className="p-3 text-center text-xs text-slate-400 italic">
                                                No tools found.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <p className="text-xs text-slate-500 pt-1">
                            The model will be copied to the selected tool with all its settings.
                        </p>
                    </div>
                </div>

                <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-end gap-2 mt-auto flex-shrink-0 rounded-b-xl">
                    <button
                        onClick={onClose}
                        disabled={isDuplicating}
                        className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isDuplicating}
                        className="px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-600/10 transition-all flex items-center gap-1.5 disabled:opacity-75 disabled:cursor-not-allowed"
                    >
                        {isDuplicating ? (
                            <>
                                <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Duplicating...
                            </>
                        ) : (
                            <>
                                <Icons.Copy className="w-3.5 h-3.5" />
                                Duplicate
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Helper: Drag Drop Uploader
const DragDropUploader = ({ accept, onUpload, currentUrl, label, uploading, height = "h-32", onRemove, disabled }) => {
    const inputRef = React.useRef(null);
    const [isDragging, setIsDragging] = React.useState(false);

    const handleDrag = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setIsDragging(true);
        } else if (e.type === "dragleave") {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            onUpload({ target: { files: [file] } });
        }
    };

    const handleClick = () => {
        if (!disabled && !currentUrl) inputRef.current.click();
    };

    return (
        <div className="w-full">
            <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={handleClick}
                className={`relative w-full ${height} rounded-xl border-2 border-dashed flex items-center justify-center transition-all overflow-hidden group
                    ${disabled
                        ? "border-slate-200 bg-slate-100 cursor-not-allowed"
                        : isDragging
                            ? "border-blue-500 bg-blue-50"
                            : "border-slate-300 bg-slate-50 hover:border-blue-400 hover:bg-slate-100 cursor-pointer"
                    }`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    onChange={onUpload}
                    disabled={uploading || disabled}
                    className="hidden"
                />

                {uploading ? (
                    <div className="flex flex-col items-center justify-center text-blue-600 animate-pulse">
                        <Icons.Loader2 className="animate-spin mb-2" />
                        <span className="text-xs font-bold">Uploading...</span>
                    </div>
                ) : currentUrl ? (
                    <div className="w-full h-full relative">
                        {/* Trash Button - Only if onRemove provided */}
                        {onRemove && !disabled && (
                            <button
                                onClick={(e) => { e.stopPropagation(); onRemove(); }}
                                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-md hover:bg-red-600 transition-colors z-20 hover:scale-110"
                                title="Hapus Asset"
                            >
                                <Icons.Trash2 size={12} />
                            </button>
                        )}

                        {/* Media Preview */}
                        {accept.startsWith("video") || currentUrl.endsWith(".mp4") ? (
                            <video
                                src={currentUrl.startsWith('http') ? currentUrl : supabase.storage.from('assets').getPublicUrl(currentUrl).data.publicUrl}
                                className="w-full h-full object-cover"
                                autoPlay muted loop
                            />
                        ) : (
                            <img
                                src={currentUrl.startsWith('http') ? currentUrl : supabase.storage.from('assets').getPublicUrl(currentUrl).data.publicUrl}
                                className="w-full h-full object-cover"
                                alt="Preview"
                            />
                        )}
                        {!disabled && (
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 text-white font-bold text-xs pointer-events-none">
                                Preview Mode
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex items-center justify-center gap-2 text-slate-400 group-hover:text-blue-500 transition-colors px-4 text-center">
                        <Icons.UploadCloud className={`w-5 h-5 shrink-0 ${disabled ? 'opacity-20' : 'opacity-50'}`} />
                        <div className="flex flex-col items-start">
                            <span className={`text-xs font-semibold ${disabled ? 'opacity-50' : ''}`}>{label}</span>
                            {!disabled && <span className="text-[9px] opacity-75">Drag & Drop or Click</span>}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
